import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  ListItemIcon,
  Select,
  TextField,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material';
import api from '../../services/api';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { alpha, useTheme, type SxProps, type Theme } from '@mui/material/styles';
import SplitPane from '../layout/SplitPane';
import {
  contentHasMedia,
  isContentCategoryEmptyForDisplay,
  getContentMediaIndicatorTooltip,
  canDeleteCategoryUnderParent,
} from '../../utils/CategoryUtils';
import { Category } from '../../types/Category';
import { Content } from '../../types/Content';
import {
  DocumentCompareSession,
  DocumentCompareSide,
  CompareNodeStatus,
  MergeCompareContentSelection,
  MergeCompareTypeConflictDirection,
  SelectiveMergeBaseline,
  SelectiveMergeSourceSide,
  classifyCompareWorkspace,
  typeConflictMergeTargetsForRows,
  TypeConflictMergeTarget,
} from '../../types/DocumentCompare';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../../types/MergeTypes';
import {
  buildCategoryPathMap,
  buildComparedChildCategoryRows,
  buildComparedContentRows,
  buildComparedSubtreeContentRows,
  buildSingleSideChildCategoryRows,
  buildSingleSideContentRows,
  buildTreeCategoryCopyToLeft,
  buildTreeCategoryCopyToRight,
  collectSubtreeContentEntries,
  comparedCategoryCanSelectForMerge,
  comparedCategorySelectionKey,
  comparedRowCanSelectForMerge,
  comparedRowSelectionKey,
  collectSideCompareSubtreePathKeys,
  COMPARE_ROOT_PATH_KEY,
  ComparedContentRow,
  ComparedContentRowStatus,
  compareTreeNodeId,
  expandedPathKeysToNodeIds,
  findCategoryByPath,
  applyTypeConflictResolvedOnSide,
  branchSubtreeHasCompareDifferences,
  compareDocumentsFullyMatch,
  comparePathSelectedOnSide,
  compareSideShowsMirroredSelection,
  compareTreeSelectedNodeId,
  getNodeCompareStatus,
  getNodeCompareStatusForSide,
  CompareNodeStatusOptions,
  hasOneSidedBranchAncestor,
  pathHasTypeConflict,
  categoryIsBranchCategory,
  categoryIsContentCategory,
  listSideCompareTreeChildren,
  nodeIdToPathKey,
  relPathToKey,
} from '../../utils/documentCompareUtils';
import { DropdownFieldTooltip } from '../DropdownFieldTooltip';
import ContentRichPreviewDialog, {
  type ContentRichPreviewView,
} from '../content/ContentRichPreviewDialog';
import {
  getContentPanePreviewText,
  rawStringHasPreviewableBody,
} from '../../utils/contentRichPreviewHtml';
import { useHelpContent } from '../../utils/HelpUtils';
import { HelpContentKey } from '../../constants/helpContent';

/** Minimum subcategories that must be selected to copy a one-sided branch selectively. */
const MIN_ONE_SIDED_BRANCH_SUBCATEGORIES = 2;

/** Shown instead of Left only / Right only under a branch that exists on one side only. */
const ONE_SIDED_BRANCH_SIDE_LABEL = 'This side only';

const compareContentStatusHelpKey: Record<ComparedContentRowStatus, HelpContentKey> = {
  both: 'document-merge-compare-content-both',
  'left-only': 'document-merge-compare-content-left-only',
  'right-only': 'document-merge-compare-content-right-only',
};

const ALL_CONTENT_ROW_STATUSES: ComparedContentRowStatus[] = [
  'left-only',
  'both',
  'right-only',
];

function CompareHelpTooltip({
  helpKey,
  enabled = true,
  placement = 'top',
  block = false,
  children,
}: {
  helpKey: HelpContentKey;
  enabled?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  block?: boolean;
  children: React.ReactElement;
}) {
  const { getTooltipTextForState } = useHelpContent();
  const title = getTooltipTextForState(helpKey, enabled);
  if (!title) {
    return children;
  }
  return (
    <Tooltip title={title} placement={placement}>
      <Box
        component="span"
        sx={{
          display: block ? 'block' : 'inline-flex',
          height: block ? '100%' : undefined,
          width: block ? '100%' : undefined,
          minHeight: block ? 0 : undefined,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

const compareDocumentTreeViewSx = (
  theme: Theme,
  options?: { selectionStrength?: 'active' | 'mirror' },
): SxProps<Theme> => {
  const strength = options?.selectionStrength;
  const selectedBg =
    strength === 'active'
      ? alpha(theme.palette.primary.main, 0.28)
      : strength === 'mirror'
        ? alpha(theme.palette.primary.main, 0.1)
        : alpha(theme.palette.primary.main, 0.1);
  const selectedHoverBg =
    strength === 'active'
      ? alpha(theme.palette.primary.main, 0.34)
      : strength === 'mirror'
        ? alpha(theme.palette.primary.main, 0.14)
        : alpha(theme.palette.primary.main, 0.14);
  return {
    '& .MuiTreeItem-content': {
      padding: theme.spacing(0, 0.5),
      minHeight: 28,
      borderRadius: 1,
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected': {
        backgroundColor: `${selectedBg} !important`,
        boxShadow:
          strength === 'active'
            ? `inset 3px 0 0 ${theme.palette.primary.main}`
            : strength === 'mirror'
              ? `inset 2px 0 0 ${alpha(theme.palette.primary.main, 0.45)}`
              : undefined,
        fontWeight: strength === 'active' ? 600 : undefined,
        color: strength === 'mirror' ? theme.palette.text.secondary : undefined,
        '&:hover': {
          backgroundColor: `${selectedHoverBg} !important`,
        },
        '&.Mui-focused': {
          backgroundColor: `${selectedHoverBg} !important`,
        },
      },
    },
    '& .MuiTreeItem-group': {
      marginLeft: 0,
      paddingLeft: theme.spacing(2),
    },
  };
};

interface DocumentCompareViewProps {
  session: DocumentCompareSession;
  onMergeSelectedContent?: (selection: MergeCompareContentSelection) => Promise<void>;
  /** Undo the most recent compare action (copy or delete) on the target document. */
  onUndoLastCopy?: () => Promise<void>;
  canUndoLastCopy?: boolean;
  onMergeSettingsChange?: (
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => void;
  mergeApplying?: boolean;
  canDeleteOnSide?: (side: 'left' | 'right') => boolean;
  onDeleteContent?: (
    side: 'left' | 'right',
    content: Content,
    categoryId: string,
    parentName: string,
  ) => void;
  onDeleteCategory?: (
    side: 'left' | 'right',
    category: Category,
    parentName: string,
    parentId: string | null,
    index: number,
  ) => void;
  /** When set, the view shows result (left) vs active source (right) for selective merge. */
  selectiveMerge?: {
    baseline: SelectiveMergeBaseline;
    sideALabel: string;
    sideBLabel: string;
    activeSourceSide: SelectiveMergeSourceSide;
    onActiveSourceSideChange?: (side: SelectiveMergeSourceSide) => void;
  };
}

const statusLabel: Record<CompareNodeStatus, string> = {
  match: 'Match',
  'has-child-diff': 'Has differences',
  'missing-content': 'Missing content',
  'has-extra-content': 'Has extra',
  'left-only': 'Left only',
  'right-only': 'Right only',
  'type-conflict': 'Type conflict',
  'type-conflict-resolved': 'Resolved',
  unpaired: '',
};

const statusColor: Record<
  CompareNodeStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  match: 'success',
  'has-child-diff': 'warning',
  'missing-content': 'warning',
  'has-extra-content': 'warning',
  'left-only': 'info',
  'right-only': 'warning',
  'type-conflict': 'error',
  'type-conflict-resolved': 'success',
  unpaired: 'default',
};

const contentStatusLabel: Record<ComparedContentRowStatus, string> = {
  both: 'Both',
  'left-only': 'Left only',
  'right-only': 'Right only',
};

function compareContentFilterLabel(
  status: ComparedContentRowStatus,
  oneWayFromSourceLabels: boolean,
  newDocumentWorkspace: boolean,
): string {
  if (oneWayFromSourceLabels) {
    if (status === 'right-only') {
      return newDocumentWorkspace ? 'Source only' : 'Shared only';
    }
    if (status === 'left-only') {
      return newDocumentWorkspace ? 'Target only' : 'Yours only';
    }
    return 'Match';
  }
  return contentStatusLabel[status];
}

const contentStatusChipColor: Record<
  ComparedContentRowStatus,
  'default' | 'success' | 'warning' | 'info'
> = {
  both: 'success',
  'left-only': 'info',
  'right-only': 'warning',
};

/** View-only trees label source-only nodes as has-extra-content, not right-only. */
function isTreeSourceOnlySelection(
  status: CompareNodeStatus | null,
  oneWayFromSource: boolean,
  pairedAtPath: boolean,
): boolean {
  if (pairedAtPath) {
    return false;
  }
  return (
    status === 'right-only' || (oneWayFromSource && status === 'has-extra-content')
  );
}

/** View-only target trees label missing nodes as missing-content, not left-only. */
function isTreeTargetOnlySelection(
  status: CompareNodeStatus | null,
  oneWayFromSource: boolean,
  pairedAtPath: boolean,
): boolean {
  if (pairedAtPath) {
    return false;
  }
  return (
    status === 'left-only' || (oneWayFromSource && status === 'missing-content')
  );
}

/** Heavy compare-direction arrows (clearer than thin ← → in compact toolbar buttons). */
const COMPARE_ARROW_LEFT = '◀';
const COMPARE_ARROW_RIGHT = '▶';

function resolveCompareContentDeleteTarget(
  row: ComparedContentRow,
  side: 'left' | 'right',
  selectedLeft: Category | null | undefined,
  selectedRight: Category | null | undefined,
): { content: Content; categoryId: string; parentName: string } | null {
  if (side === 'left') {
    if (row.status === 'right-only') {
      return null;
    }
    const categoryId = row.leftContentCategoryId || selectedLeft?.id;
    if (!categoryId) {
      return null;
    }
    const parentName =
      row.categoryRelPath?.length
        ? row.categoryRelPath[row.categoryRelPath.length - 1]
        : selectedLeft?.name ?? 'category';
    return { content: row.content, categoryId, parentName };
  }
  if (row.status === 'left-only') {
    return null;
  }
  const content = row.status === 'both' && row.rightContent ? row.rightContent : row.content;
  const categoryId = row.rightContentCategoryId || selectedRight?.id;
  if (!categoryId) {
    return null;
  }
  const parentName =
    row.categoryRelPath?.length
      ? row.categoryRelPath[row.categoryRelPath.length - 1]
      : selectedRight?.name ?? 'category';
  return { content, categoryId, parentName };
}

function CompareDirectionLabel({
  direction,
  children,
}: {
  direction: 'left' | 'right';
  children: React.ReactNode;
}) {
  const arrow = (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        fontWeight: 900,
        fontSize: '1.08em',
        lineHeight: 1,
        mr: direction === 'left' ? 0.35 : 0,
        ml: direction === 'right' ? 0.35 : 0,
      }}
    >
      {direction === 'left' ? COMPARE_ARROW_LEFT : COMPARE_ARROW_RIGHT}
    </Box>
  );
  if (direction === 'left') {
    return (
      <>
        {arrow}
        {children}
      </>
    );
  }
  return (
    <>
      {children}
      {arrow}
    </>
  );
}

function mergeToLeftActionLabel(count?: number): React.ReactNode {
  const text = count != null ? `Merge to left (${count})` : 'Merge to left';
  return <CompareDirectionLabel direction="left">{text}</CompareDirectionLabel>;
}

function mergeToRightActionLabel(count?: number): React.ReactNode {
  const text = count != null ? `Merge to right (${count})` : 'Merge to right';
  return <CompareDirectionLabel direction="right">{text}</CompareDirectionLabel>;
}

function mergeToYourDocumentLabel(): React.ReactNode {
  return (
    <CompareDirectionLabel direction="left">Merge to your document</CompareDirectionLabel>
  );
}

function mergeIntoTargetLabel(): React.ReactNode {
  return (
    <CompareDirectionLabel direction="left">Merge into target</CompareDirectionLabel>
  );
}

function copyToLeftActionLabel(text: string): React.ReactNode {
  return <CompareDirectionLabel direction="left">{text}</CompareDirectionLabel>;
}

function copyToRightActionLabel(text: string): React.ReactNode {
  return <CompareDirectionLabel direction="right">{text}</CompareDirectionLabel>;
}

type CompareTreePaneRole = 'target' | 'source';

function CompareTreePaneHeader({
  side,
  documentLabel,
  readOnly,
  role,
  isActiveSelectionSide = false,
}: {
  side: 'left' | 'right';
  documentLabel: string;
  readOnly: boolean;
  role?: CompareTreePaneRole;
  isActiveSelectionSide?: boolean;
}) {
  const theme = useTheme();
  const { getTooltipTextForState } = useHelpContent();
  const readOnlyHint = getTooltipTextForState('document-merge-compare-view-only-side', true);

  return (
    <Box
      data-testid={`compare-tree-header-${side}`}
      aria-label={
        readOnly
          ? `${documentLabel}, view only`
          : role === 'target'
            ? `${documentLabel}, target document`
            : documentLabel
      }
      sx={{
        px: 1,
        py: 0.5,
        borderBottom: 2,
        borderColor: isActiveSelectionSide ? 'primary.main' : 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexShrink: 0,
        minWidth: 0,
        bgcolor: isActiveSelectionSide
          ? alpha(theme.palette.primary.main, 0.1)
          : readOnly
            ? alpha(theme.palette.info.main, 0.07)
            : 'background.paper',
      }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{
          fontWeight: 600,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={documentLabel}
      >
        {documentLabel}
      </Typography>
      {role === 'target' ? (
        <Chip
          size="small"
          label="Target"
          color="primary"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }}
        />
      ) : null}
      {readOnly ? (
        <Tooltip title={readOnlyHint || 'View only'} placement="bottom">
          <Chip
            size="small"
            icon={<LockOutlinedIcon sx={{ fontSize: '14px !important' }} />}
            label="View only"
            color="info"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              flexShrink: 0,
              '& .MuiChip-icon': { ml: 0.5 },
            }}
          />
        </Tooltip>
      ) : null}
    </Box>
  );
}

function formatContentPreview(content: Content): string {
  const text = getContentPanePreviewText(content.text, content.richTextHtml).trim();
  if (text) {
    return text.length > 100 ? `${text.substring(0, 100)}...` : text;
  }
  return contentHasMedia(content) ? '(media only)' : '';
}

function contentPreviewSourceHtml(content: Content): string {
  return (content.richTextHtml || content.text || '').trim();
}

function compareSidePreviewLabels(
  oneWayFromSourceLabels: boolean,
  newDocumentWorkspace: boolean,
): { left: string; right: string } {
  if (oneWayFromSourceLabels) {
    return newDocumentWorkspace
      ? { left: 'Target', right: 'Source' }
      : { left: 'Your document', right: 'Shared document' };
  }
  return { left: 'Left', right: 'Right' };
}

function compareRowPreviewViews(
  row: ComparedContentRow,
  oneWayFromSourceLabels: boolean,
  newDocumentWorkspace: boolean,
): ContentRichPreviewView[] {
  const { left: leftLabel, right: rightLabel } = compareSidePreviewLabels(
    oneWayFromSourceLabels,
    newDocumentWorkspace,
  );
  const views: ContentRichPreviewView[] = [];
  const addIfPreviewable = (label: string, content: Content) => {
    const html = contentPreviewSourceHtml(content);
    if (rawStringHasPreviewableBody(html)) {
      views.push({ label, html });
    }
  };

  if (row.status === 'left-only') {
    addIfPreviewable(leftLabel, row.content);
  } else if (row.status === 'right-only') {
    addIfPreviewable(rightLabel, row.content);
  } else {
    addIfPreviewable(leftLabel, row.content);
    if (row.rightContent) {
      const rightHtml = contentPreviewSourceHtml(row.rightContent);
      const leftHtml = contentPreviewSourceHtml(row.content);
      if (rawStringHasPreviewableBody(rightHtml) && rightHtml !== leftHtml) {
        views.push({ label: rightLabel, html: rightHtml });
      }
    }
  }
  return views;
}

function compareRowHasPreviewableContent(row: ComparedContentRow): boolean {
  if (row.status === 'both' && row.rightContent) {
    const leftHtml = contentPreviewSourceHtml(row.content);
    const rightHtml = contentPreviewSourceHtml(row.rightContent);
    return (
      rawStringHasPreviewableBody(leftHtml) || rawStringHasPreviewableBody(rightHtml)
    );
  }
  return rawStringHasPreviewableBody(contentPreviewSourceHtml(row.content));
}

function renderCompareChildCategoryTypeIcon(
  category: Category | undefined,
  compareStatus?: CompareNodeStatus,
) {
  if (compareStatus === 'type-conflict') {
    return (
      <ReportProblemOutlinedIcon
        sx={{ color: 'error.main', fontSize: 20 }}
        aria-label="Type conflict"
      />
    );
  }
  if (!category) {
    return null;
  }
  if (category.contentCategory) {
    return isContentCategoryEmptyForDisplay(category, null) ? (
      <DescriptionOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
    ) : (
      <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
    );
  }
  const hasChildren = (category.children?.length ?? 0) > 0;
  return hasChildren ? (
    <FolderIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
  ) : (
    <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
  );
}

function CompareContentStatusChip({
  status,
  oneWayLabels = false,
  newDocumentWorkspace = false,
  underOneSidedBranch = false,
}: {
  status: ComparedContentRowStatus;
  oneWayLabels?: boolean;
  newDocumentWorkspace?: boolean;
  underOneSidedBranch?: boolean;
}) {
  const { getTooltipTextForState } = useHelpContent();
  const isSideOnlyStatus = status === 'left-only' || status === 'right-only';
  const helpKey =
    underOneSidedBranch && isSideOnlyStatus
      ? 'document-merge-compare-content-one-sided-branch'
      : compareContentStatusHelpKey[status];
  let label = contentStatusLabel[status];
  if (underOneSidedBranch && isSideOnlyStatus) {
    label = ONE_SIDED_BRANCH_SIDE_LABEL;
  } else if (oneWayLabels) {
    if (status === 'right-only') {
      label = newDocumentWorkspace ? 'Not in target' : 'Not in your document';
    } else if (status === 'left-only') {
      label = newDocumentWorkspace ? 'Only in target' : 'Only in your document';
    } else if (status === 'both') {
      label = 'Match';
    }
  }
  const chip = (
    <Chip
      size="small"
      label={label}
      color={
        underOneSidedBranch && isSideOnlyStatus
          ? 'default'
          : contentStatusChipColor[status]
      }
      variant="outlined"
      sx={{ height: 22, fontSize: '0.7rem' }}
    />
  );
  const title = getTooltipTextForState(helpKey, true);
  if (!title) {
    return chip;
  }
  return (
    <Tooltip title={title} placement="top">
      <span style={{ display: 'inline-flex' }}>{chip}</span>
    </Tooltip>
  );
}

function CompareStatusChip({
  status,
  oneWayLabels = false,
  newDocumentWorkspace = false,
  existsOnTarget = true,
  underOneSidedBranch = false,
}: {
  status: CompareNodeStatus;
  /** One-way compare (view-only source or new-document target workspace). */
  oneWayLabels?: boolean;
  newDocumentWorkspace?: boolean;
  /** False when this path has no category on the left/target document. */
  existsOnTarget?: boolean;
  underOneSidedBranch?: boolean;
}) {
  if (status === 'unpaired') {
    return null;
  }
  const isSideOnlyStatus = status === 'left-only' || status === 'right-only';
  let label = statusLabel[status];
  if (underOneSidedBranch && isSideOnlyStatus) {
    label = ONE_SIDED_BRANCH_SIDE_LABEL;
  } else if (oneWayLabels) {
    if (status === 'left-only') {
      label = newDocumentWorkspace ? 'Only in target' : 'Only in your document';
    } else if (status === 'has-extra-content' && !existsOnTarget) {
      // Source-only paths (no target category): Not in target. Paired target surplus keeps "Has extra".
      label = newDocumentWorkspace ? 'Not in target' : 'Not in your document';
    }
  }
  return (
    <Chip
      size="small"
      label={label}
      color={underOneSidedBranch && isSideOnlyStatus ? 'default' : statusColor[status]}
      variant="outlined"
      sx={{ ml: 0.75, height: 20, fontSize: '0.7rem' }}
    />
  );
}

function compareSideStatusOptions(
  side: 'left' | 'right',
  oneWayFromSource: boolean,
  extra?: Omit<CompareNodeStatusOptions, 'sourceOnlySide' | 'targetOnlySide'>,
): CompareNodeStatusOptions {
  return {
    ...extra,
    sourceOnlySide: side === 'right' && oneWayFromSource,
    targetOnlySide: side === 'left' && oneWayFromSource,
  };
}

function renderTreeNodes(
  leftRoot: Category,
  rightRoot: Category,
  parentRelPath: string[],
  side: 'left' | 'right',
  selectedTreeSide: 'left' | 'right',
  selectedPath: string[] | null,
  onSelectPath: (path: string[]) => void,
  onRowDoubleClick: (relPath: string[], hasChildren: boolean, e: React.MouseEvent) => void,
  oneWayFromSourceLabels: boolean,
  newDocumentWorkspace: boolean,
  typeConflictMergedSides: Record<string, TypeConflictMergeTarget[]> | undefined,
  onTreeContextMenu?: (e: React.MouseEvent, side: 'left' | 'right', path: string[]) => void,
): React.ReactNode[] {
  const sideRoot = side === 'left' ? leftRoot : rightRoot;
  const children = listSideCompareTreeChildren(sideRoot, parentRelPath);

  return children.map(({ displayName, relPath: childPath }) => {
    const leftNode = findCategoryByPath(leftRoot, childPath) ?? undefined;
    const rightNode = findCategoryByPath(rightRoot, childPath) ?? undefined;
    const parentTypeConflict = pathHasTypeConflict(leftRoot, rightRoot, parentRelPath);
    const status = applyTypeConflictResolvedOnSide(
      getNodeCompareStatusForSide(leftNode, rightNode, side, {
        parentTypeConflict,
        treeContext: { leftRoot, rightRoot, relPath: childPath },
        ...compareSideStatusOptions(side, oneWayFromSourceLabels),
      }),
      childPath,
      side,
      typeConflictMergedSides,
    );
    const node = side === 'left' ? leftNode : rightNode;
    if (!node) {
      return null;
    }
    const pathKey = childPath.join('/');
    const hasBranchChildren = listSideCompareTreeChildren(sideRoot, childPath).length > 0;
    const hasChildren = hasBranchChildren && !node.contentCategory;

    const label = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.25,
        }}
        onClick={(e) => {
          e.stopPropagation();
          onSelectPath(childPath);
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!hasChildren) {
            return;
          }
          onRowDoubleClick(childPath, true, e);
        }}
        onContextMenu={
          onTreeContextMenu
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                onTreeContextMenu(e, side, childPath);
              }
            : undefined
        }
      >
        {node.contentCategory ? (
          <DescriptionIcon sx={{ fontSize: 18, mr: 0.5, opacity: 0.8 }} />
        ) : (
          <FolderIcon sx={{ fontSize: 18, mr: 0.5, opacity: 0.8 }} />
        )}
        <Typography variant="body2" component="span">
          {displayName}
        </Typography>
        <CompareStatusChip
          status={status}
          oneWayLabels={oneWayFromSourceLabels}
          newDocumentWorkspace={newDocumentWorkspace}
          existsOnTarget={leftNode != null}
          underOneSidedBranch={hasOneSidedBranchAncestor(leftRoot, rightRoot, childPath)}
        />
      </Box>
    );

    const nodeId = compareTreeNodeId(side, node.id);

    if (node.contentCategory || !hasChildren) {
      return <TreeItem key={nodeId} nodeId={nodeId} label={label} />;
    }

    return (
      <TreeItem key={nodeId} nodeId={nodeId} label={label}>
        {renderTreeNodes(
          leftRoot,
          rightRoot,
          childPath,
          side,
          selectedTreeSide,
          selectedPath,
          onSelectPath,
          onRowDoubleClick,
          oneWayFromSourceLabels,
          newDocumentWorkspace,
          typeConflictMergedSides,
          onTreeContextMenu,
        )}
      </TreeItem>
    );
  }).filter((node): node is React.ReactElement => node != null);
}

const DocumentCompareView: React.FC<DocumentCompareViewProps> = ({
  session,
  onMergeSelectedContent,
  onUndoLastCopy,
  canUndoLastCopy = false,
  onMergeSettingsChange,
  mergeApplying = false,
  canDeleteOnSide,
  onDeleteContent,
  onDeleteCategory,
  selectiveMerge,
}) => {
  const isSelectiveMerge = selectiveMerge != null;
  const rightIsReadOnlyShared = session.right.readAccessGrantId != null;
  /** Left-only rows copy to the right; disabled in selective merge (result ← source only). */
  const copyToRightDisabled = rightIsReadOnlyShared || isSelectiveMerge;
  const workspaceKind = classifyCompareWorkspace(
    isSelectiveMerge,
    session.right.readAccessGrantId,
  );
  const oneWayFromSourceLabels = workspaceKind !== 'two-way';
  const resolveMissing =
    session.resolveMissing ?? MergeMissingResolve.COPY;
  const resolveTypeMismatch =
    session.resolveTypeMismatch ?? MergeTypeMismatchResolve.KEEP;
  const theme = useTheme();
  const { getTooltipTextForState } = useHelpContent();
  const [selectedPath, setSelectedPath] = useState<string[] | null>(null);
  const [selectedTreeSide, setSelectedTreeSide] = useState<'left' | 'right'>('left');
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [treeContextMenu, setTreeContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [contentPreview, setContentPreview] = useState<{
    title: string;
    views: ContentRichPreviewView[];
  } | null>(null);
  const [contentStatusFilters, setContentStatusFilters] = useState<
    Set<ComparedContentRowStatus>
  >(() => new Set(ALL_CONTENT_ROW_STATUSES));

  const documentsFullyMatch = useMemo(
    () => compareDocumentsFullyMatch(session.leftRoot, session.rightRoot),
    [session.leftRoot, session.rightRoot],
  );

  const isRootSelection =
    selectedPath != null && selectedPath.length === 0;

  const leftRootNodeId = compareTreeNodeId('left', session.leftRoot.id);
  const rightRootNodeId = compareTreeNodeId('right', session.rightRoot.id);

  const leftPathMap = useMemo(
    () => buildCategoryPathMap(session.leftRoot),
    [session.leftRoot],
  );
  const rightPathMap = useMemo(
    () => buildCategoryPathMap(session.rightRoot),
    [session.rightRoot],
  );

  const [expandedPathKeys, setExpandedPathKeys] = useState<string[]>(() => [COMPARE_ROOT_PATH_KEY]);

  const leftExpanded = useMemo(
    () => expandedPathKeysToNodeIds('left', session.leftRoot, expandedPathKeys),
    [expandedPathKeys, session.leftRoot],
  );
  const rightExpanded = useMemo(
    () => expandedPathKeysToNodeIds('right', session.rightRoot, expandedPathKeys),
    [expandedPathKeys, session.rightRoot],
  );

  const togglePathKey = (pathKey: string) => {
    setExpandedPathKeys((prev) =>
      prev.includes(pathKey) ? prev.filter((k) => k !== pathKey) : [...prev, pathKey],
    );
  };

  const toggleSubtreePathKeys = (relPath: string[], side: 'left' | 'right') => {
    const keys = collectSideCompareSubtreePathKeys(
      side === 'left' ? session.leftRoot : session.rightRoot,
      relPath,
    );
    if (keys.length === 0) {
      return;
    }
    setExpandedPathKeys((prev) => {
      const fullyExpanded = keys.every((k) => prev.includes(k));
      if (fullyExpanded) {
        const remove = new Set(keys);
        return prev.filter((k) => !remove.has(k));
      }
      return Array.from(new Set([...prev, ...keys]));
    });
  };

  const handleRowDoubleClick = (
    relPath: string[],
    hasChildren: boolean,
    side: 'left' | 'right',
    e: React.MouseEvent,
  ) => {
    if (!hasChildren) {
      return;
    }
    const pathKey = relPathToKey(relPath);
    if (e.metaKey || e.ctrlKey) {
      toggleSubtreePathKeys(relPath, side);
      return;
    }
    togglePathKey(pathKey);
  };

  const handleChevronToggle = (side: 'left' | 'right', nodeIds: string[]) => {
    const prevIds = side === 'left' ? leftExpanded : rightExpanded;
    const toggled =
      nodeIds.find((id) => !prevIds.includes(id)) ??
      prevIds.find((id) => !nodeIds.includes(id));
    if (!toggled) {
      return;
    }
    const rootName = side === 'left' ? session.leftRoot.name : session.rightRoot.name;
    const pathMap = side === 'left' ? leftPathMap : rightPathMap;
    const pathKey = nodeIdToPathKey(side, toggled, pathMap, rootName);
    if (pathKey) {
      togglePathKey(pathKey);
    }
  };

  const selectPathFromNodeId = (side: 'left' | 'right', nodeId: string) => {
    const rootName = side === 'left' ? session.leftRoot.name : session.rightRoot.name;
    const pathMap = side === 'left' ? leftPathMap : rightPathMap;
    const pathKey = nodeIdToPathKey(side, nodeId, pathMap, rootName);
    if (!pathKey) {
      return;
    }
    setSelectedTreeSide(side);
    if (pathKey === relPathToKey([])) {
      setSelectedPath([]);
      return;
    }
    setSelectedPath(pathKey.split('/'));
  };

  const selectPath = (side: 'left' | 'right', path: string[]) => {
    setSelectedTreeSide(side);
    setSelectedPath(path);
  };

  const handleTreeContextMenu = (
    e: React.MouseEvent,
    side: 'left' | 'right',
    path: string[],
  ) => {
    e.preventDefault();
    e.stopPropagation();
    selectPath(side, path);
    setTreeContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
  };

  const closeTreeContextMenu = () => setTreeContextMenu(null);

  const renderRootTreeItem = (
    side: 'left' | 'right',
    root: Category,
    otherRoot: Category,
    rootNodeId: string,
    rootLabel: string,
    onTreeContextMenu?: (e: React.MouseEvent, side: 'left' | 'right', path: string[]) => void,
  ) => {
    const leftNode = side === 'left' ? root : otherRoot;
    const rightNode = side === 'right' ? root : otherRoot;
    const leftDocRoot = side === 'left' ? root : otherRoot;
    const rightDocRoot = side === 'right' ? root : otherRoot;
    const status = applyTypeConflictResolvedOnSide(
      getNodeCompareStatusForSide(leftNode, rightNode, side, {
        treeContext: { leftRoot: leftDocRoot, rightRoot: rightDocRoot, relPath: [] },
        ...compareSideStatusOptions(side, oneWayFromSourceLabels),
      }),
      [],
      side,
      session.typeConflictMergedSides,
    );
    const hasSubtree = listSideCompareTreeChildren(root, []).length > 0;

    const label = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.25,
        }}
        onClick={(e) => {
          e.stopPropagation();
          selectPath(side, []);
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!hasSubtree) {
            return;
          }
          handleRowDoubleClick([], hasSubtree, side, e);
        }}
        onContextMenu={
          onTreeContextMenu
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                onTreeContextMenu(e, side, []);
              }
            : undefined
        }
      >
        <FolderIcon sx={{ fontSize: 18, mr: 0.5, opacity: 0.8 }} />
        <Typography variant="body2" component="span">
          {rootLabel}
        </Typography>
        <CompareStatusChip
          status={status}
          oneWayLabels={oneWayFromSourceLabels}
          newDocumentWorkspace={workspaceKind === 'new-document'}
          existsOnTarget={leftNode != null}
        />
      </Box>
    );

    if (!hasSubtree) {
      return <TreeItem nodeId={rootNodeId} label={label} />;
    }

    return (
      <TreeItem nodeId={rootNodeId} label={label}>
        {renderTreeNodes(
          leftNode,
          rightNode,
          [],
          side,
          selectedTreeSide,
          selectedPath,
          (path) => selectPath(side, path),
          (relPath, hasChildren, e) => handleRowDoubleClick(relPath, hasChildren, side, e),
          oneWayFromSourceLabels,
          workspaceKind === 'new-document',
          session.typeConflictMergedSides,
          onTreeContextMenu,
        )}
      </TreeItem>
    );
  };

  const selectedLeft =
    selectedPath == null
      ? null
      : selectedPath.length === 0
        ? session.leftRoot
        : findCategoryByPath(session.leftRoot, selectedPath);
  const selectedRight =
    selectedPath == null
      ? null
      : selectedPath.length === 0
        ? session.rightRoot
        : findCategoryByPath(session.rightRoot, selectedPath);

  const selectedCategoryOnActiveSide =
    selectedTreeSide === 'left' ? selectedLeft : selectedRight;

  const selectedCategoryDeleteContext = useMemo(() => {
    if (
      !onDeleteCategory ||
      !canDeleteOnSide?.(selectedTreeSide) ||
      selectedPath == null ||
      selectedPath.length === 0 ||
      !selectedCategoryOnActiveSide
    ) {
      return null;
    }
    const sideRoot = selectedTreeSide === 'left' ? session.leftRoot : session.rightRoot;
    const parentPath = selectedPath.slice(0, -1);
    const parent =
      parentPath.length === 0 ? sideRoot : findCategoryByPath(sideRoot, parentPath);
    if (!parent) {
      return null;
    }
    const index = parent.children?.findIndex((c) => c.id === selectedCategoryOnActiveSide.id) ?? -1;
    const allowed = canDeleteCategoryUnderParent(parent, selectedCategoryOnActiveSide.id);
    return {
      side: selectedTreeSide,
      category: selectedCategoryOnActiveSide,
      parentName: parent.name,
      parentId: parent.id,
      index,
      allowed,
    };
  }, [
    canDeleteOnSide,
    onDeleteCategory,
    selectedCategoryOnActiveSide,
    selectedPath,
    selectedTreeSide,
    session.leftRoot,
    session.rightRoot,
  ]);

  const showContentDeleteActions = !!onDeleteContent;

  const leftTreeSelectedNodeId = useMemo(
    () =>
      compareTreeSelectedNodeId(
        'left',
        session.leftRoot,
        selectedPath,
        selectedTreeSide,
        session.leftRoot,
        session.rightRoot,
      ),
    [selectedPath, selectedTreeSide, session.leftRoot, session.rightRoot],
  );
  const rightTreeSelectedNodeId = useMemo(
    () =>
      compareTreeSelectedNodeId(
        'right',
        session.rightRoot,
        selectedPath,
        selectedTreeSide,
        session.leftRoot,
        session.rightRoot,
      ),
    [selectedPath, selectedTreeSide, session.leftRoot, session.rightRoot],
  );

  const isSingleSidedCategorySelection =
    (selectedLeft != null) !== (selectedRight != null);

  const isTypeConflictSelection = useMemo(
    () =>
      selectedLeft != null &&
      selectedRight != null &&
      selectedLeft.contentCategory !== selectedRight.contentCategory,
    [selectedLeft, selectedRight],
  );

  const treeSelectionStatus = useMemo((): CompareNodeStatus | null => {
    if (selectedPath == null || selectedPath.length === 0) {
      return null;
    }
    const parentTypeConflict =
      selectedPath.length > 0 &&
      pathHasTypeConflict(
        session.leftRoot,
        session.rightRoot,
        selectedPath.slice(0, -1),
      );
    return getNodeCompareStatusForSide(
      selectedLeft ?? undefined,
      selectedRight ?? undefined,
      selectedTreeSide,
      {
        parentTypeConflict,
        ...compareSideStatusOptions(selectedTreeSide, oneWayFromSourceLabels),
      },
    );
  }, [
    selectedPath,
    selectedLeft,
    selectedRight,
    selectedTreeSide,
    session.leftRoot,
    session.rightRoot,
    oneWayFromSourceLabels,
  ]);

  const treeSelectionRowKey =
    selectedPath != null && selectedPath.length > 0 ? relPathToKey(selectedPath) : '';

  const treeSelectionPaired =
    selectedLeft != null && selectedRight != null;

  const treeCategoryCopyToLeft = useMemo(() => {
    if (
      !isTreeSourceOnlySelection(
        treeSelectionStatus,
        oneWayFromSourceLabels,
        treeSelectionPaired,
      ) ||
      !selectedRight ||
      selectedPath == null
    ) {
      return null;
    }
    return buildTreeCategoryCopyToLeft(
      session.leftRoot,
      selectedPath,
      selectedRight,
    );
  }, [
    treeSelectionStatus,
    selectedRight,
    selectedPath,
    session.leftRoot,
    oneWayFromSourceLabels,
    treeSelectionPaired,
  ]);

  const treeCategoryCopyToRight = useMemo(() => {
    if (
      !isTreeTargetOnlySelection(
        treeSelectionStatus,
        oneWayFromSourceLabels,
        treeSelectionPaired,
      ) ||
      !selectedLeft ||
      copyToRightDisabled ||
      selectedPath == null
    ) {
      return null;
    }
    return buildTreeCategoryCopyToRight(
      session.rightRoot,
      selectedPath,
      selectedLeft,
    );
  }, [
    treeSelectionStatus,
    selectedLeft,
    selectedPath,
    session.rightRoot,
    copyToRightDisabled,
    oneWayFromSourceLabels,
    treeSelectionPaired,
  ]);

  const pairedBranchMergeTarget = useMemo(() => {
    if (
      !selectedLeft ||
      !selectedRight ||
      selectedLeft.contentCategory ||
      selectedRight.contentCategory ||
      isTypeConflictSelection ||
      selectedPath == null ||
      isTreeTargetOnlySelection(
        treeSelectionStatus,
        oneWayFromSourceLabels,
        treeSelectionPaired,
      ) ||
      isTreeSourceOnlySelection(
        treeSelectionStatus,
        oneWayFromSourceLabels,
        treeSelectionPaired,
      )
    ) {
      return null;
    }
    const branchPath = selectedPath ?? [];
    if (
      !branchSubtreeHasCompareDifferences(session.leftRoot, session.rightRoot, branchPath)
    ) {
      return null;
    }
    return {
      left: {
        mergeIntoCategoryId: selectedLeft.id,
        sourceCategoryId: selectedRight.id,
        treatIntoAsParent: false,
      },
      right: {
        mergeIntoCategoryId: selectedRight.id,
        sourceCategoryId: selectedLeft.id,
        treatIntoAsParent: false,
      },
    };
  }, [
    selectedLeft,
    selectedRight,
    isTypeConflictSelection,
    selectedPath,
    treeSelectionStatus,
    session.leftRoot,
    session.rightRoot,
    oneWayFromSourceLabels,
    treeSelectionPaired,
  ]);

  const pairedContentMergeTargets = useMemo(() => {
    if (
      !selectedLeft?.contentCategory ||
      !selectedRight?.contentCategory ||
      isTypeConflictSelection ||
      selectedPath == null ||
      isTreeTargetOnlySelection(
        treeSelectionStatus,
        oneWayFromSourceLabels,
        treeSelectionPaired,
      ) ||
      isTreeSourceOnlySelection(
        treeSelectionStatus,
        oneWayFromSourceLabels,
        treeSelectionPaired,
      )
    ) {
      return null;
    }
    if (getNodeCompareStatus(selectedLeft, selectedRight) === 'match') {
      return null;
    }
    const leftMissing = getNodeCompareStatusForSide(
      selectedLeft,
      selectedRight,
      'left',
    ) === 'missing-content';
    const rightMissing = getNodeCompareStatusForSide(
      selectedLeft,
      selectedRight,
      'right',
    ) === 'missing-content';
    return {
      left: leftMissing
        ? {
            mergeIntoCategoryId: selectedLeft.id,
            sourceCategoryId: selectedRight.id,
            treatIntoAsParent: false,
            explicitCopy: true,
          }
        : null,
      right: rightMissing
        ? {
            mergeIntoCategoryId: selectedRight.id,
            sourceCategoryId: selectedLeft.id,
            treatIntoAsParent: false,
            explicitCopy: true,
          }
        : null,
    };
  }, [
    selectedLeft,
    selectedRight,
    isTypeConflictSelection,
    selectedPath,
    treeSelectionStatus,
    oneWayFromSourceLabels,
    treeSelectionPaired,
  ]);

  const treeCategorySubtreeToLeft = useMemo(() => {
    if (treeCategoryCopyToLeft) {
      return {
        mergeIntoCategoryId: treeCategoryCopyToLeft.targetParentCategoryId,
        sourceCategoryId: treeCategoryCopyToLeft.sourceCategoryId,
        treatIntoAsParent: true,
      };
    }
    return (
      pairedContentMergeTargets?.left ?? pairedBranchMergeTarget?.left ?? null
    );
  }, [treeCategoryCopyToLeft, pairedContentMergeTargets, pairedBranchMergeTarget]);

  const treeCategorySubtreeToRight = useMemo(() => {
    if (treeCategoryCopyToRight) {
      return {
        mergeIntoCategoryId: treeCategoryCopyToRight.targetParentCategoryId,
        sourceCategoryId: treeCategoryCopyToRight.sourceCategoryId,
        treatIntoAsParent: true,
      };
    }
    if (rightIsReadOnlyShared || isSelectiveMerge) {
      return null;
    }
    return (
      pairedContentMergeTargets?.right ?? pairedBranchMergeTarget?.right ?? null
    );
  }, [
    treeCategoryCopyToRight,
    pairedContentMergeTargets,
    pairedBranchMergeTarget,
    rightIsReadOnlyShared,
    isSelectiveMerge,
  ]);

  /** Paired subtree diffs can merge either way; show merge into the opposite side from the selection. */
  const headerMergeBothDirectionsAvailable =
    treeCategorySubtreeToLeft != null && treeCategorySubtreeToRight != null;
  const showHeaderMergeToLeft =
    treeCategorySubtreeToLeft != null &&
    (workspaceKind !== 'new-document' || isRootSelection || selectedTreeSide === 'right') &&
    (!headerMergeBothDirectionsAvailable || selectedTreeSide === 'right');
  const showHeaderMergeToRight =
    treeCategorySubtreeToRight != null &&
    (!headerMergeBothDirectionsAvailable || selectedTreeSide === 'left');

  /** Missing / Type mismatch apply only to recursive merge on paired branch categories. */
  const showMergeResolutionControls =
    pairedBranchMergeTarget != null ||
    (isRootSelection && !documentsFullyMatch);

  const categoryPanePath = useMemo(() => {
    if (selectedPath == null) {
      return null;
    }
    return selectedPath;
  }, [selectedPath]);

  const categoryPaneLeft = useMemo(
    () =>
      categoryPanePath == null
        ? undefined
        : findCategoryByPath(session.leftRoot, categoryPanePath),
    [categoryPanePath, session.leftRoot],
  );

  const categoryPaneRight = useMemo(
    () =>
      categoryPanePath == null
        ? undefined
        : findCategoryByPath(session.rightRoot, categoryPanePath),
    [categoryPanePath, session.rightRoot],
  );

  const singleSidedBranchCopyPlan = useMemo(() => {
    if (
      !isSingleSidedCategorySelection ||
      selectedPath == null ||
      selectedPath.length === 0
    ) {
      return null;
    }
    const sole = selectedLeft ?? selectedRight;
    if (!sole || categoryIsContentCategory(sole)) {
      return null;
    }
    if (
      !isTreeTargetOnlySelection(treeSelectionStatus, oneWayFromSourceLabels, false) &&
      !isTreeSourceOnlySelection(treeSelectionStatus, oneWayFromSourceLabels, false)
    ) {
      return null;
    }
    if (
      isTreeSourceOnlySelection(treeSelectionStatus, oneWayFromSourceLabels, false) &&
      selectedRight &&
      selectedPath != null
    ) {
      const target = buildTreeCategoryCopyToLeft(
        session.leftRoot,
        selectedPath,
        selectedRight,
      );
      if (!target) {
        return null;
      }
      return {
        direction: 'left' as const,
        branchPath: [...selectedPath],
        targetParentCategoryId: target.targetParentCategoryId,
        newCategoryName: selectedRight.name,
      };
    }
    if (
      isTreeTargetOnlySelection(treeSelectionStatus, oneWayFromSourceLabels, false) &&
      selectedLeft &&
      !copyToRightDisabled &&
      selectedPath != null
    ) {
      const target = buildTreeCategoryCopyToRight(
        session.rightRoot,
        selectedPath,
        selectedLeft,
      );
      if (!target) {
        return null;
      }
      return {
        direction: 'right' as const,
        branchPath: [...selectedPath],
        targetParentCategoryId: target.targetParentCategoryId,
        newCategoryName: selectedLeft.name,
      };
    }
    return null;
  }, [
    isSingleSidedCategorySelection,
    selectedPath,
    selectedLeft,
    selectedRight,
    treeSelectionStatus,
    session.leftRoot,
    session.rightRoot,
    copyToRightDisabled,
    oneWayFromSourceLabels,
  ]);

  const singleSidedBranchCopyView = useMemo(() => {
    if (!singleSidedBranchCopyPlan || selectedPath == null) {
      return false;
    }
    if (
      relPathToKey(selectedPath) !==
      relPathToKey(singleSidedBranchCopyPlan.branchPath)
    ) {
      return false;
    }
    return !hasOneSidedBranchAncestor(
      session.leftRoot,
      session.rightRoot,
      selectedPath,
    );
  }, [
    singleSidedBranchCopyPlan,
    selectedPath,
    session.leftRoot,
    session.rightRoot,
  ]);

  const canMergeCompare = useMemo(() => {
    if (selectedPath == null) {
      return false;
    }
    const sole = selectedLeft ?? selectedRight;
    const standaloneSingleSidedContentCopy =
      isSingleSidedCategorySelection &&
      categoryIsContentCategory(sole) &&
      !hasOneSidedBranchAncestor(
        session.leftRoot,
        session.rightRoot,
        selectedPath,
      );
    if (standaloneSingleSidedContentCopy) {
      return true;
    }
    if (singleSidedBranchCopyView) {
      return true;
    }
    if (!categoryPaneLeft || !categoryPaneRight) {
      return false;
    }
    return categoryPaneLeft.contentCategory === categoryPaneRight.contentCategory;
  }, [
    selectedPath,
    categoryPaneLeft,
    categoryPaneRight,
    isSingleSidedCategorySelection,
    selectedLeft,
    selectedRight,
    session.leftRoot,
    session.rightRoot,
    singleSidedBranchCopyView,
  ]);

  const singleSidedContentCopyPlan = useMemo(() => {
    if (!isSingleSidedCategorySelection || selectedPath == null) {
      return null;
    }
    if (
      hasOneSidedBranchAncestor(
        session.leftRoot,
        session.rightRoot,
        selectedPath,
      )
    ) {
      return null;
    }

    if (selectedLeft?.contentCategory && !selectedRight) {
      if (copyToRightDisabled) {
        return null;
      }
      const parentPath = selectedPath.slice(0, -1);
      const targetParent =
        parentPath.length === 0
          ? session.rightRoot
          : findCategoryByPath(session.rightRoot, parentPath);
      if (!targetParent || targetParent.contentCategory) {
        return null;
      }
      return {
        direction: 'right' as const,
        targetParentCategoryId: targetParent.id,
        newCategoryName: selectedLeft.name,
      };
    }

    if (selectedRight?.contentCategory && !selectedLeft) {
      const parentPath = selectedPath.slice(0, -1);
      const targetParent =
        parentPath.length === 0
          ? session.leftRoot
          : findCategoryByPath(session.leftRoot, parentPath);
      if (!targetParent || targetParent.contentCategory) {
        return null;
      }
      return {
        direction: 'left' as const,
        targetParentCategoryId: targetParent.id,
        newCategoryName: selectedRight.name,
      };
    }

    return null;
  }, [
    isSingleSidedCategorySelection,
    selectedPath,
    selectedLeft,
    selectedRight,
    session.leftRoot,
    session.rightRoot,
    copyToRightDisabled,
  ]);

  const paneCategory = useMemo(() => {
    if (selectedPath == null) {
      return null;
    }
    const root = selectedTreeSide === 'left' ? session.leftRoot : session.rightRoot;
    return selectedPath.length === 0 ? root : findCategoryByPath(root, selectedPath);
  }, [selectedPath, selectedTreeSide, session.leftRoot, session.rightRoot]);

  const comparePaneMode = useMemo((): 'categories' | 'content' | null => {
    if (selectedPath == null || (!selectedLeft && !selectedRight)) {
      return null;
    }
    if (isTypeConflictSelection && paneCategory) {
      if (paneCategory.contentCategory) {
        return 'content';
      }
      return 'categories';
    }
    if (isSingleSidedCategorySelection) {
      const sole = selectedLeft ?? selectedRight!;
      if (sole.contentCategory) {
        return 'content';
      }
      return 'categories';
    }
    if (
      (selectedLeft && !selectedLeft.contentCategory) ||
      (selectedRight && !selectedRight.contentCategory)
    ) {
      return 'categories';
    }
    return 'content';
  }, [
    selectedPath,
    selectedLeft,
    selectedRight,
    isSingleSidedCategorySelection,
    isTypeConflictSelection,
    paneCategory,
  ]);

  /** Paired content categories share the same content pane; side click does not change what is shown. */
  const selectionIsSideAgnostic = useMemo(() => {
    if (
      selectedPath == null ||
      isTypeConflictSelection ||
      !selectedLeft ||
      !selectedRight
    ) {
      return false;
    }
    return selectedLeft.contentCategory && selectedRight.contentCategory;
  }, [selectedPath, isTypeConflictSelection, selectedLeft, selectedRight]);

  const underOneSidedBranchContext = useMemo(
    () =>
      selectedPath != null &&
      hasOneSidedBranchAncestor(
        session.leftRoot,
        session.rightRoot,
        selectedPath,
      ),
    [selectedPath, session.leftRoot, session.rightRoot],
  );

  const viewingContentUnderSingleSidedBranch = useMemo(
    () => comparePaneMode === 'content' && underOneSidedBranchContext,
    [comparePaneMode, underOneSidedBranchContext],
  );

  const comparedCategoryRows = useMemo(() => {
    if (comparePaneMode !== 'categories' || categoryPanePath == null) {
      return null;
    }
    if (isTypeConflictSelection && paneCategory) {
      return buildSingleSideChildCategoryRows(
        categoryPanePath,
        paneCategory,
        selectedTreeSide,
      );
    }
    return buildComparedChildCategoryRows(
      session.leftRoot,
      session.rightRoot,
      categoryPanePath,
      selectedTreeSide,
      oneWayFromSourceLabels
        ? {
            sourceOnlySide: selectedTreeSide === 'right' ? true : undefined,
            targetOnlySide: selectedTreeSide === 'left' ? true : undefined,
          }
        : undefined,
    );
  }, [
    comparePaneMode,
    categoryPanePath,
    isTypeConflictSelection,
    paneCategory,
    selectedTreeSide,
    session.leftRoot,
    session.rightRoot,
    oneWayFromSourceLabels,
  ]);

  const comparedContentRows = useMemo(() => {
    if (comparePaneMode !== 'content' || selectedPath == null) {
      return null;
    }
    if (isTypeConflictSelection && paneCategory) {
      return buildSingleSideContentRows(paneCategory, selectedTreeSide);
    }
    if (isSingleSidedCategorySelection) {
      const leftEntries = selectedLeft ? collectSubtreeContentEntries(selectedLeft) : [];
      const rightEntries = selectedRight ? collectSubtreeContentEntries(selectedRight) : [];
      return buildComparedSubtreeContentRows(
        leftEntries,
        rightEntries,
        () => undefined,
        () => undefined,
      );
    }
    return buildComparedContentRows(selectedLeft?.contents, selectedRight?.contents);
  }, [
    comparePaneMode,
    selectedPath,
    selectedLeft,
    selectedRight,
    isSingleSidedCategorySelection,
    isTypeConflictSelection,
    paneCategory,
  ]);

  const selectedPathKey = selectedPath == null ? '' : relPathToKey(selectedPath);

  useEffect(() => {
    setContentStatusFilters(new Set(ALL_CONTENT_ROW_STATUSES));
  }, [selectedPathKey, comparePaneMode]);

  const showContentCategoryColumn = useMemo(
    () =>
      (isSingleSidedCategorySelection || isTypeConflictSelection) &&
      (comparedContentRows?.some((row) => (row.categoryRelPath?.length ?? 0) > 0) ?? false),
    [comparedContentRows, isSingleSidedCategorySelection, isTypeConflictSelection],
  );

  const rightOnlyRowKeys = useMemo(() => {
    if (!comparedContentRows) {
      return [];
    }
    return comparedContentRows
      .filter((row) => row.status === 'right-only')
      .map((row) => comparedRowSelectionKey(row));
  }, [comparedContentRows]);

  const leftOnlyRowKeys = useMemo(() => {
    if (!comparedContentRows || copyToRightDisabled) {
      return [];
    }
    return comparedContentRows
      .filter((row) => row.status === 'left-only')
      .map((row) => comparedRowSelectionKey(row));
  }, [comparedContentRows, copyToRightDisabled]);

  const mergeableRowKeys = useMemo(
    () => [...rightOnlyRowKeys, ...leftOnlyRowKeys],
    [rightOnlyRowKeys, leftOnlyRowKeys],
  );

  const rightOnlyCategoryRowKeys = useMemo(() => {
    if (!comparedCategoryRows) {
      return [];
    }
    return comparedCategoryRows
      .filter((row) => row.status === 'right-only')
      .map((row) => comparedCategorySelectionKey(row));
  }, [comparedCategoryRows]);

  const leftOnlyCategoryRowKeys = useMemo(() => {
    if (!comparedCategoryRows || copyToRightDisabled) {
      return [];
    }
    return comparedCategoryRows
      .filter((row) => row.status === 'left-only')
      .map((row) => comparedCategorySelectionKey(row));
  }, [comparedCategoryRows, copyToRightDisabled]);

  const mergeableCategoryRowKeys = useMemo(
    () => [...rightOnlyCategoryRowKeys, ...leftOnlyCategoryRowKeys],
    [rightOnlyCategoryRowKeys, leftOnlyCategoryRowKeys],
  );

  const typeConflictCategoryRowKeys = useMemo(() => {
    if (!comparedCategoryRows) {
      return [];
    }
    return comparedCategoryRows
      .filter((row) => row.status === 'type-conflict')
      .map((row) => comparedCategorySelectionKey(row));
  }, [comparedCategoryRows]);

  const selectedTypeConflictTableRows = useMemo(() => {
    if (!comparedCategoryRows) {
      return [];
    }
    return comparedCategoryRows.filter(
      (row) =>
        row.status === 'type-conflict' &&
        row.leftCategory &&
        row.rightCategory &&
        selectedRowKeys.has(comparedCategorySelectionKey(row)),
    );
  }, [comparedCategoryRows, selectedRowKeys]);

  const typeConflictRowsForActions = useMemo(() => {
    if (selectedTypeConflictTableRows.length > 0) {
      return selectedTypeConflictTableRows;
    }
    if (
      isTypeConflictSelection &&
      selectedLeft &&
      selectedRight &&
      selectedPath != null &&
      selectedPath.length > 0
    ) {
      return [
        {
          status: 'type-conflict' as const,
          key: treeSelectionRowKey,
          displayName: selectedPath[selectedPath.length - 1],
          childRelPath: selectedPath,
          leftCategory: selectedLeft,
          rightCategory: selectedRight,
        },
      ];
    }
    return [];
  }, [
    selectedTypeConflictTableRows,
    isTypeConflictSelection,
    selectedLeft,
    selectedRight,
    selectedPath,
    treeSelectionRowKey,
  ]);

  const typeConflictMergeActions = useMemo(
    () =>
      typeConflictMergeTargetsForRows(
        session.typeConflictMergedSides,
        typeConflictRowsForActions,
      ),
    [session.typeConflictMergedSides, typeConflictRowsForActions],
  );

  const activeRightOnlyKeys =
    comparePaneMode === 'categories' ? rightOnlyCategoryRowKeys : rightOnlyRowKeys;
  const activeLeftOnlyKeys =
    comparePaneMode === 'categories' ? leftOnlyCategoryRowKeys : leftOnlyRowKeys;
  const activeMergeableKeys =
    comparePaneMode === 'categories' ? mergeableCategoryRowKeys : mergeableRowKeys;

  useEffect(() => {
    // UX: when selecting a one-sided content category, pre-select all rows so the user can
    // copy "the whole category" by default and then unselect what they don't want.
    if (
      onMergeSelectedContent &&
      isSingleSidedCategorySelection &&
      comparePaneMode === 'content' &&
      comparedContentRows &&
      !hasOneSidedBranchAncestor(
        session.leftRoot,
        session.rightRoot,
        selectedPath ?? [],
      )
    ) {
      const next = new Set<string>();
      for (const row of comparedContentRows) {
        if (comparedRowCanSelectForMerge(row, copyToRightDisabled)) {
          next.add(comparedRowSelectionKey(row));
        }
      }
      setSelectedRowKeys(next);
      return;
    }
    setSelectedRowKeys(new Set());
  }, [selectedPathKey]);

  const selectedCopyToLeftIds = useMemo(() => {
    if (!comparedContentRows) {
      return [];
    }
    return comparedContentRows
      .filter(
        (row) =>
          row.status === 'right-only' && selectedRowKeys.has(comparedRowSelectionKey(row)),
      )
      .map((row) => row.content.id);
  }, [comparedContentRows, selectedRowKeys]);

  const selectedCopyToRightIds = useMemo(() => {
    if (!comparedContentRows || copyToRightDisabled) {
      return [];
    }
    return comparedContentRows
      .filter(
        (row) =>
          row.status === 'left-only' && selectedRowKeys.has(comparedRowSelectionKey(row)),
      )
      .map((row) => row.content.id);
  }, [comparedContentRows, selectedRowKeys, copyToRightDisabled]);

  const selectedCopyToLeftCategoryIds = useMemo(() => {
    if (!comparedCategoryRows) {
      return [];
    }
    return comparedCategoryRows
      .filter(
        (row) =>
          row.status === 'right-only' &&
          row.rightCategory &&
          selectedRowKeys.has(comparedCategorySelectionKey(row)),
      )
      .map((row) => row.rightCategory!.id);
  }, [comparedCategoryRows, selectedRowKeys]);

  const selectedCopyToRightCategoryIds = useMemo(() => {
    if (!comparedCategoryRows || copyToRightDisabled) {
      return [];
    }
    return comparedCategoryRows
      .filter(
        (row) =>
          row.status === 'left-only' &&
          row.leftCategory &&
          selectedRowKeys.has(comparedCategorySelectionKey(row)),
      )
      .map((row) => row.leftCategory!.id);
  }, [comparedCategoryRows, selectedRowKeys, copyToRightDisabled]);

  const toggleRowSelection = (selectionKey: string) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(selectionKey)) {
        next.delete(selectionKey);
      } else {
        next.add(selectionKey);
      }
      return next;
    });
  };

  const toggleSelectAllWithStatus = (status: 'right-only' | 'left-only') => {
    const keys = status === 'right-only' ? activeRightOnlyKeys : activeLeftOnlyKeys;
    if (keys.length === 0) {
      return;
    }
    const allSelected = keys.every((key) => selectedRowKeys.has(key));
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const key of keys) {
          next.delete(key);
        }
      } else {
        for (const key of keys) {
          next.add(key);
        }
      }
      return next;
    });
  };

  const allRightOnlySelected =
    activeRightOnlyKeys.length > 0 &&
    activeRightOnlyKeys.every((key) => selectedRowKeys.has(key));
  const allLeftOnlySelected =
    activeLeftOnlyKeys.length > 0 &&
    activeLeftOnlyKeys.every((key) => selectedRowKeys.has(key));
  const someRightOnlySelected =
    !allRightOnlySelected &&
    activeRightOnlyKeys.some((key) => selectedRowKeys.has(key));
  const someLeftOnlySelected =
    !allLeftOnlySelected &&
    activeLeftOnlyKeys.some((key) => selectedRowKeys.has(key));

  const effectiveCategoryCopyToLeft = useMemo(() => {
    if (
      selectedCopyToLeftCategoryIds.length > 0 &&
      categoryIsBranchCategory(categoryPaneLeft)
    ) {
      return {
        targetParentCategoryId: categoryPaneLeft!.id,
        sourceCategoryIds: selectedCopyToLeftCategoryIds,
      };
    }
    if (treeCategoryCopyToLeft && selectedCopyToLeftCategoryIds.length === 0) {
      return {
        targetParentCategoryId: treeCategoryCopyToLeft.targetParentCategoryId,
        sourceCategoryIds: [treeCategoryCopyToLeft.sourceCategoryId],
      };
    }
    return null;
  }, [
    selectedCopyToLeftCategoryIds,
    categoryPaneLeft,
    treeCategoryCopyToLeft,
  ]);

  const effectiveCategoryCopyToRight = useMemo(() => {
    if (
      selectedCopyToRightCategoryIds.length > 0 &&
      categoryIsBranchCategory(categoryPaneRight)
    ) {
      return {
        targetParentCategoryId: categoryPaneRight!.id,
        sourceCategoryIds: selectedCopyToRightCategoryIds,
      };
    }
    if (treeCategoryCopyToRight && selectedCopyToRightCategoryIds.length === 0) {
      return {
        targetParentCategoryId: treeCategoryCopyToRight.targetParentCategoryId,
        sourceCategoryIds: [treeCategoryCopyToRight.sourceCategoryId],
      };
    }
    return null;
  }, [
    selectedCopyToRightCategoryIds,
    categoryPaneRight,
    treeCategoryCopyToRight,
  ]);

  const effectiveContentCopyToLeft = useMemo(() => {
    if (selectedCopyToLeftIds.length > 0 && selectedLeft?.contentCategory) {
      return {
        targetCategoryId: selectedLeft.id,
        sourceContentIds: selectedCopyToLeftIds,
      };
    }
    return null;
  }, [selectedCopyToLeftIds, selectedLeft]);

  const effectiveContentCopyToRight = useMemo(() => {
    if (selectedCopyToRightIds.length > 0 && selectedRight?.contentCategory) {
      return {
        targetCategoryId: selectedRight.id,
        sourceContentIds: selectedCopyToRightIds,
      };
    }
    return null;
  }, [selectedCopyToRightIds, selectedRight]);

  const copyToLeftCount = useMemo(() => {
    if (singleSidedContentCopyPlan?.direction === 'left') {
      return selectedCopyToLeftIds.length;
    }
    if (
      singleSidedBranchCopyView &&
      singleSidedBranchCopyPlan?.direction === 'left'
    ) {
      return selectedCopyToLeftCategoryIds.length;
    }
    return (
      (effectiveCategoryCopyToLeft?.sourceCategoryIds.length ?? 0) +
      (effectiveContentCopyToLeft?.sourceContentIds.length ?? 0)
    );
  }, [
    singleSidedContentCopyPlan,
    singleSidedBranchCopyView,
    singleSidedBranchCopyPlan,
    selectedCopyToLeftIds,
    selectedCopyToLeftCategoryIds,
    effectiveCategoryCopyToLeft,
    effectiveContentCopyToLeft,
  ]);

  const copyToRightCount = useMemo(() => {
    if (singleSidedContentCopyPlan?.direction === 'right') {
      return selectedCopyToRightIds.length;
    }
    if (
      singleSidedBranchCopyView &&
      singleSidedBranchCopyPlan?.direction === 'right'
    ) {
      return selectedCopyToRightCategoryIds.length;
    }
    return (
      (effectiveCategoryCopyToRight?.sourceCategoryIds.length ?? 0) +
      (effectiveContentCopyToRight?.sourceContentIds.length ?? 0)
    );
  }, [
    singleSidedContentCopyPlan,
    singleSidedBranchCopyView,
    singleSidedBranchCopyPlan,
    selectedCopyToRightIds,
    selectedCopyToRightCategoryIds,
    effectiveCategoryCopyToRight,
    effectiveContentCopyToRight,
  ]);

  const copyToLeftButtonLabel = mergeApplying
    ? workspaceKind === 'new-document'
      ? 'Adding…'
      : 'Copying…'
    : workspaceKind === 'new-document'
      ? copyToLeftActionLabel(`Add to target (${copyToLeftCount})`)
      : workspaceKind === 'view-only-source' &&
          singleSidedContentCopyPlan?.direction === 'left' &&
          selectedCopyToLeftIds.length === 0
        ? copyToLeftActionLabel('Copy category to your document')
        : workspaceKind === 'view-only-source'
          ? copyToLeftActionLabel(`Copy to your document (${copyToLeftCount})`)
          : singleSidedContentCopyPlan?.direction === 'left' &&
              selectedCopyToLeftIds.length === 0
            ? copyToLeftActionLabel('Copy category only to left')
            : copyToLeftActionLabel(`Copy to left (${copyToLeftCount})`);

  const copyToRightButtonLabel = mergeApplying
    ? 'Copying…'
    : singleSidedContentCopyPlan?.direction === 'right' &&
        selectedCopyToRightIds.length === 0
      ? copyToRightActionLabel('Copy category only to right')
      : copyToRightActionLabel(`Copy to right (${copyToRightCount})`);

  const isSingleSidedBranchCopyLeft =
    singleSidedBranchCopyView && singleSidedBranchCopyPlan?.direction === 'left';
  const isSingleSidedBranchCopyRight =
    singleSidedBranchCopyView && singleSidedBranchCopyPlan?.direction === 'right';
  const branchSubcategoriesReadyForCopyLeft =
    isSingleSidedBranchCopyLeft &&
    selectedCopyToLeftCategoryIds.length >= MIN_ONE_SIDED_BRANCH_SUBCATEGORIES;
  const branchSubcategoriesReadyForCopyRight =
    isSingleSidedBranchCopyRight &&
    selectedCopyToRightCategoryIds.length >= MIN_ONE_SIDED_BRANCH_SUBCATEGORIES;

  const showCopyToLeftButton =
    singleSidedContentCopyPlan?.direction === 'left' ||
    branchSubcategoriesReadyForCopyLeft ||
    (!singleSidedBranchCopyView &&
      copyToLeftCount > 0 &&
      (comparePaneMode !== 'content' || contentStatusFilters.has('right-only')));
  const showCopyToRightButton =
    !isSelectiveMerge &&
    (singleSidedContentCopyPlan?.direction === 'right' ||
      branchSubcategoriesReadyForCopyRight ||
      (!singleSidedBranchCopyView &&
        copyToRightCount > 0 &&
        (comparePaneMode !== 'content' || contentStatusFilters.has('left-only'))));

  const handleCopyToLeft = async () => {
    const copyCategoryOnly = singleSidedContentCopyPlan?.direction === 'left';
    const copyBranchWithSubs = branchSubcategoriesReadyForCopyLeft;
    if (
      !onMergeSelectedContent ||
      (!copyCategoryOnly && !copyBranchWithSubs && copyToLeftCount === 0)
    ) {
      return;
    }
    try {
      if (copyBranchWithSubs && singleSidedBranchCopyPlan) {
        await onMergeSelectedContent({
          copyToLeft: {
            createBranchShell: {
              parentCategoryId: singleSidedBranchCopyPlan.targetParentCategoryId,
              name: singleSidedBranchCopyPlan.newCategoryName,
            },
            sourceCategoryIds: selectedCopyToLeftCategoryIds,
          },
        });
      } else if (
        singleSidedContentCopyPlan?.direction === 'left'
      ) {
        const created = await api.post<Category>('/categories', {
          parentId: singleSidedContentCopyPlan.targetParentCategoryId,
          name: singleSidedContentCopyPlan.newCategoryName,
          description: '',
        });
        await onMergeSelectedContent({
          copyToLeft: {
            targetCategoryId: created.data.id,
            sourceContentIds: selectedCopyToLeftIds,
            newlyCreatedCategoryId: created.data.id,
          },
        });
      } else if (effectiveCategoryCopyToLeft) {
        await onMergeSelectedContent({
          copyToLeft: effectiveCategoryCopyToLeft,
        });
      } else if (effectiveContentCopyToLeft) {
        await onMergeSelectedContent({
          copyToLeft: effectiveContentCopyToLeft,
        });
      }
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const handleCopyToRight = async () => {
    const copyCategoryOnly = singleSidedContentCopyPlan?.direction === 'right';
    const copyBranchWithSubs = branchSubcategoriesReadyForCopyRight;
    if (
      !onMergeSelectedContent ||
      rightIsReadOnlyShared ||
      (!copyCategoryOnly && !copyBranchWithSubs && copyToRightCount === 0)
    ) {
      return;
    }
    try {
      if (copyBranchWithSubs && singleSidedBranchCopyPlan) {
        await onMergeSelectedContent({
          copyToRight: {
            createBranchShell: {
              parentCategoryId: singleSidedBranchCopyPlan.targetParentCategoryId,
              name: singleSidedBranchCopyPlan.newCategoryName,
            },
            sourceCategoryIds: selectedCopyToRightCategoryIds,
          },
        });
      } else if (
        singleSidedContentCopyPlan?.direction === 'right'
      ) {
        const created = await api.post<Category>('/categories', {
          parentId: singleSidedContentCopyPlan.targetParentCategoryId,
          name: singleSidedContentCopyPlan.newCategoryName,
          description: '',
        });
        await onMergeSelectedContent({
          copyToRight: {
            targetCategoryId: created.data.id,
            sourceContentIds: selectedCopyToRightIds,
            newlyCreatedCategoryId: created.data.id,
          },
        });
      } else if (effectiveCategoryCopyToRight) {
        await onMergeSelectedContent({
          copyToRight: effectiveCategoryCopyToRight,
        });
      } else if (effectiveContentCopyToRight) {
        await onMergeSelectedContent({
          copyToRight: effectiveContentCopyToRight,
        });
      }
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const paneTitle = comparePaneMode === 'categories' ? 'Categories' : 'Content';
  const activeDocumentLabel =
    selectedTreeSide === 'left' ? session.left.label : session.right.label;
  const hasTreeSelection = selectedPath != null;
  const selectedSideRoleLabel = useMemo((): string | null => {
    if (selectedPath == null || selectionIsSideAgnostic) {
      return null;
    }
    if (workspaceKind === 'new-document') {
      return selectedTreeSide === 'left' ? 'Target' : 'Source';
    }
    if (workspaceKind === 'view-only-source') {
      return selectedTreeSide === 'left' ? 'Your document' : 'View only';
    }
    return selectedTreeSide === 'left' ? 'Left' : 'Right';
  }, [selectedPath, selectionIsSideAgnostic, workspaceKind, selectedTreeSide]);
  const panePathLabel = useMemo(() => {
    const pathLabel =
      selectedPath != null
        ? selectedPath.length === 0
          ? selectedTreeSide === 'left'
            ? session.leftRoot.name
            : session.rightRoot.name
          : selectedPath.join(' / ')
        : '';
    if (!pathLabel) {
      return '';
    }
    if (selectionIsSideAgnostic) {
      return pathLabel;
    }
    const fromLabel = selectedSideRoleLabel
      ? `${selectedSideRoleLabel}: ${activeDocumentLabel}`
      : activeDocumentLabel;
    return `${fromLabel} — ${pathLabel}`;
  }, [
    selectedPath,
    selectedTreeSide,
    selectionIsSideAgnostic,
    selectedSideRoleLabel,
    activeDocumentLabel,
    session.leftRoot.name,
    session.rightRoot.name,
  ]);

  const showContentStatusFilter =
    comparePaneMode === 'content' &&
    (comparedContentRows?.length ?? 0) > 0 &&
    !viewingContentUnderSingleSidedBranch;

  const allContentStatusesSelected = useMemo(
    () => ALL_CONTENT_ROW_STATUSES.every((status) => contentStatusFilters.has(status)),
    [contentStatusFilters],
  );

  const contentStatusFilterLabels = useMemo(
    () =>
      Object.fromEntries(
        ALL_CONTENT_ROW_STATUSES.map((status) => [
          status,
          compareContentFilterLabel(
            status,
            oneWayFromSourceLabels,
            workspaceKind === 'new-document',
          ),
        ]),
      ) as Record<ComparedContentRowStatus, string>,
    [oneWayFromSourceLabels, workspaceKind],
  );

  const displayedComparedContentRows = useMemo(() => {
    if (!comparedContentRows) {
      return null;
    }
    if (allContentStatusesSelected) {
      return comparedContentRows;
    }
    return comparedContentRows.filter((row) => contentStatusFilters.has(row.status));
  }, [comparedContentRows, contentStatusFilters, allContentStatusesSelected]);

  const leftTreeViewSx = useMemo(() => {
    const selectionStrength: 'active' | 'mirror' | undefined = !hasTreeSelection
      ? undefined
      : selectedTreeSide === 'left'
        ? 'active'
        : compareSideShowsMirroredSelection(
              'left',
              selectedPath,
              selectedTreeSide,
              session.leftRoot,
              session.rightRoot,
            )
          ? 'mirror'
          : undefined;
    return compareDocumentTreeViewSx(theme, { selectionStrength });
  }, [
    theme,
    hasTreeSelection,
    selectedPath,
    selectedTreeSide,
    session.leftRoot,
    session.rightRoot,
  ]);
  const rightTreeViewSx = useMemo(() => {
    const selectionStrength: 'active' | 'mirror' | undefined = !hasTreeSelection
      ? undefined
      : selectedTreeSide === 'right'
        ? 'active'
        : compareSideShowsMirroredSelection(
              'right',
              selectedPath,
              selectedTreeSide,
              session.leftRoot,
              session.rightRoot,
            )
          ? 'mirror'
          : undefined;
    return compareDocumentTreeViewSx(theme, { selectionStrength });
  }, [
    theme,
    hasTreeSelection,
    selectedPath,
    selectedTreeSide,
    session.leftRoot,
    session.rightRoot,
  ]);
  const leftTreePaneActive = hasTreeSelection && selectedTreeSide === 'left';
  const rightTreePaneActive = hasTreeSelection && selectedTreeSide === 'right';
  const showCategoryCheckboxes =
    canMergeCompare || typeConflictCategoryRowKeys.length > 0;

  const showCopyActions =
    !isTypeConflictSelection &&
    !viewingContentUnderSingleSidedBranch &&
    activeMergeableKeys.length > 0;

  const showSelectAllRight =
    showCopyActions &&
    activeRightOnlyKeys.length > 0 &&
    (comparePaneMode !== 'content' || contentStatusFilters.has('right-only'));
  const showSelectAllLeft =
    showCopyActions &&
    activeLeftOnlyKeys.length > 0 &&
    (comparePaneMode !== 'content' || contentStatusFilters.has('left-only'));

  /** Type-conflict merge for the tree selection (whole category), not row picks in the pane. */
  const showHeaderTypeConflictMerge =
    !!onMergeSelectedContent &&
    selectedPath != null &&
    selectedTypeConflictTableRows.length === 0 &&
    typeConflictRowsForActions.length > 0 &&
    (typeConflictMergeActions.canLeft || typeConflictMergeActions.canRight);

  const showHeaderTypeConflictMergeToLeft =
    showHeaderTypeConflictMerge &&
    typeConflictMergeActions.canLeft &&
    selectedTreeSide === 'right';
  const showHeaderTypeConflictMergeToRight =
    showHeaderTypeConflictMerge &&
    !isSelectiveMerge &&
    !rightIsReadOnlyShared &&
    typeConflictMergeActions.canRight &&
    selectedTreeSide === 'left';

  /** Type-conflict merge only for rows explicitly selected in the category/content table. */
  const showDetailPaneTypeConflictMerge =
    !!onMergeSelectedContent &&
    selectedTypeConflictTableRows.length > 0 &&
    typeConflictRowsForActions.length > 0 &&
    (typeConflictMergeActions.canLeft || typeConflictMergeActions.canRight);

  const showDetailPaneSelectiveActions =
    showCopyActions ||
    showDetailPaneTypeConflictMerge ||
    (canMergeCompare && activeMergeableKeys.length > 0) ||
    (canMergeCompare && typeConflictCategoryRowKeys.length > 0);

  const showDetailPaneActions =
    !!onMergeSelectedContent &&
    selectedPath != null &&
    (comparePaneMode === 'content' ||
      comparePaneMode === 'categories' ||
      isTypeConflictSelection) &&
    showDetailPaneSelectiveActions;

  const compareTreeCategoryMenuHasActions =
    !!selectedCategoryDeleteContext ||
    showHeaderMergeToLeft ||
    showHeaderMergeToRight ||
    showHeaderTypeConflictMergeToLeft ||
    showHeaderTypeConflictMergeToRight;

  const categoryPanelMessage = useMemo(() => {
    if (selectedPath == null) {
      if (documentsFullyMatch) {
        return 'Documents match. Select a category to browse.';
      }
      return 'Select a category to compare.';
    }
    if (isRootSelection && !documentsFullyMatch) {
      if (workspaceKind === 'new-document') {
        return 'Roots selected. Merge into target or pick a category.';
      }
      if (workspaceKind === 'view-only-source') {
        return 'Roots selected. Copy from the shared side or pick a category.';
      }
      return 'Roots selected. Merge to left/right or pick a category.';
    }
    return null;
  }, [selectedPath, documentsFullyMatch, isRootSelection, workspaceKind]);

  const handleMergeCategorySubtreeToLeft = async () => {
    if (mergeApplying) {
      return;
    }
    if (!onMergeSelectedContent || !treeCategorySubtreeToLeft) {
      return;
    }
    try {
      await onMergeSelectedContent({
        mergeCategorySubtreeToLeft: treeCategorySubtreeToLeft,
      });
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const handleMergeCategorySubtreeToRight = async () => {
    if (mergeApplying || rightIsReadOnlyShared) {
      return;
    }
    if (!onMergeSelectedContent || !treeCategorySubtreeToRight) {
      return;
    }
    try {
      await onMergeSelectedContent({
        mergeCategorySubtreeToRight: treeCategorySubtreeToRight,
      });
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const handleMergeTypeConflict = async (direction: MergeCompareTypeConflictDirection) => {
    if (!onMergeSelectedContent || typeConflictRowsForActions.length === 0 || mergeApplying) {
      return;
    }
    const rowsToMerge = typeConflictRowsForActions.filter((row) => {
      const done = session.typeConflictMergedSides?.[row.key] ?? [];
      if (direction === 'left') {
        return !done.includes('left');
      }
      return !done.includes(direction);
    });
    if (rowsToMerge.length === 0) {
      return;
    }
    try {
      await onMergeSelectedContent({
        typeConflict: {
          direction,
          rows: rowsToMerge.map((row) => ({
            rowKey: row.key,
            leftCategoryId: row.leftCategory!.id,
            rightCategoryId: row.rightCategory!.id,
          })),
        },
      });
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const categoryRowBackground = (status: CompareNodeStatus) => {
    switch (status) {
      case 'match':
        return alpha(theme.palette.success.main, 0.1);
      case 'has-child-diff':
        return alpha(theme.palette.warning.main, 0.12);
      case 'missing-content':
      case 'has-extra-content':
        return alpha(theme.palette.warning.main, 0.12);
      case 'left-only':
        return alpha(theme.palette.info.main, 0.12);
      case 'right-only':
        return alpha(theme.palette.warning.main, 0.12);
      case 'type-conflict':
        return alpha(theme.palette.error.main, 0.1);
      case 'type-conflict-resolved':
        return alpha(theme.palette.success.main, 0.1);
      case 'unpaired':
        return undefined;
    }
  };

  const showContentMatchColumn =
    canMergeCompare || viewingContentUnderSingleSidedBranch;

  const contentRowBackground = (status: ComparedContentRowStatus) => {
    if (viewingContentUnderSingleSidedBranch) {
      return undefined;
    }
    switch (status) {
      case 'both':
        return alpha(theme.palette.success.main, 0.1);
      case 'left-only':
        return alpha(theme.palette.info.main, 0.12);
      case 'right-only':
        return alpha(theme.palette.warning.main, 0.12);
    }
  };

  const contentStatusFilterButtonSx = (
    status: ComparedContentRowStatus | 'all',
    selected: boolean,
  ) => {
    const paletteKey =
      status === 'all'
        ? 'primary'
        : status === 'left-only'
          ? 'info'
          : status === 'both'
            ? 'success'
            : 'warning';
    const main = theme.palette[paletteKey].main;
    const dark = theme.palette[paletteKey].dark;
    const base = {
      px: 0.75,
      py: 0.125,
      fontSize: '0.7rem',
      textTransform: 'none' as const,
      lineHeight: 1.3,
      border: `1px solid ${alpha(main, selected ? 0.55 : 0.38)} !important`,
    };
    if (selected) {
      return {
        ...base,
        fontWeight: 600,
        bgcolor: `${alpha(main, 0.28)} !important`,
        color: `${dark} !important`,
        '&:hover': {
          bgcolor: `${alpha(main, 0.36)} !important`,
        },
        '&.Mui-selected': {
          bgcolor: `${alpha(main, 0.28)} !important`,
          color: `${dark} !important`,
          '&:hover': {
            bgcolor: `${alpha(main, 0.36)} !important`,
          },
        },
      };
    }
    return {
      ...base,
      fontWeight: 400,
      bgcolor:
        status === 'all'
          ? 'transparent'
          : `${contentRowBackground(status)} !important`,
      color: 'text.secondary',
      '&:hover': {
        bgcolor: `${alpha(main, 0.18)} !important`,
        color: `${dark} !important`,
      },
      '&.Mui-selected': {
        bgcolor: `${alpha(main, 0.28)} !important`,
        color: `${dark} !important`,
      },
    };
  };

  const contentStatusFilterToggle = showContentStatusFilter ? (
    <CompareHelpTooltip helpKey="document-merge-compare-content-filter" placement="bottom">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          flexWrap: 'wrap',
          ml: 'auto',
          pl: 1.5,
          borderLeft: 1,
          borderColor: 'divider',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}
        >
          Show rows:
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <ToggleButton
            size="small"
            value="all"
            selected={allContentStatusesSelected}
            onClick={() => setContentStatusFilters(new Set(ALL_CONTENT_ROW_STATUSES))}
            sx={contentStatusFilterButtonSx('all', allContentStatusesSelected)}
          >
            All
          </ToggleButton>
          <ToggleButtonGroup
            size="small"
            exclusive={false}
            value={Array.from(contentStatusFilters)}
            onChange={(_, values: ComparedContentRowStatus[]) => {
              setContentStatusFilters(new Set(values));
            }}
          >
            {ALL_CONTENT_ROW_STATUSES.map((status) => (
              <ToggleButton
                key={status}
                value={status}
                aria-label={contentStatusFilterLabels[status]}
                sx={contentStatusFilterButtonSx(status, contentStatusFilters.has(status))}
              >
                {contentStatusFilterLabels[status]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
    </CompareHelpTooltip>
  ) : null;

  const openCompareContentPreview = useCallback(
    (row: ComparedContentRow) => {
      const views = compareRowPreviewViews(
        row,
        oneWayFromSourceLabels,
        workspaceKind === 'new-document',
      );
      if (views.length === 0) {
        return;
      }
      const subcase = row.content.subcase || '(no subcase)';
      const pathSuffix =
        row.categoryRelPath && row.categoryRelPath.length > 0
          ? ` — ${row.categoryRelPath.join(' / ')}`
          : '';
      setContentPreview({
        title: `${subcase}${pathSuffix}`,
        views,
      });
      (document.activeElement as HTMLElement | null)?.blur?.();
    },
    [oneWayFromSourceLabels, workspaceKind],
  );

  const selectAllSideButtonSx = (
    side: 'right-only' | 'left-only',
    state: 'none' | 'some' | 'all',
  ) => {
    const paletteKey = side === 'right-only' ? 'warning' : 'info';
    const main = theme.palette[paletteKey].main;
    const dark = theme.palette[paletteKey].dark;
    const base = {
      minWidth: 0,
      px: 1,
      py: 0.125,
      fontSize: '0.7rem',
      textTransform: 'none' as const,
    };
    if (state === 'all') {
      return {
        ...base,
        fontWeight: 600,
        border: `1px solid ${alpha(main, 0.55)}`,
        bgcolor: alpha(main, 0.28),
        color: dark,
        boxShadow: `inset 0 0 0 1px ${alpha(main, 0.15)}`,
        '&:hover': { bgcolor: alpha(main, 0.36) },
      };
    }
    if (state === 'some') {
      return {
        ...base,
        fontWeight: 500,
        border: `1px dashed ${alpha(main, 0.65)}`,
        bgcolor: alpha(main, 0.16),
        color: 'text.primary',
        '&:hover': { bgcolor: alpha(main, 0.22) },
      };
    }
    return {
      ...base,
      fontWeight: 400,
      border: `1px solid ${alpha(main, 0.35)}`,
      bgcolor: contentRowBackground(side),
      color: 'text.secondary',
      '&:hover': { bgcolor: alpha(main, 0.18), color: dark },
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
          overflowX: 'auto',
        }}
      >
        {workspaceKind === 'two-way' && documentsFullyMatch ? (
          <CompareHelpTooltip helpKey="document-merge-compare" placement="bottom">
            <Typography
              variant="caption"
              color="success.main"
              component="span"
              sx={{ fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              These documents are identical.
            </Typography>
          </CompareHelpTooltip>
        ) : null}
        {isSelectiveMerge && selectiveMerge?.baseline === 'intersection' &&
        selectiveMerge.onActiveSourceSideChange ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Source document:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={selectiveMerge.activeSourceSide}
              onChange={(_, value: SelectiveMergeSourceSide | null) => {
                if (value) {
                  selectiveMerge.onActiveSourceSideChange?.(value);
                }
              }}
            >
              <ToggleButton value="a">{selectiveMerge.sideALabel}</ToggleButton>
              <ToggleButton value="b">{selectiveMerge.sideBLabel}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ) : null}
        {onMergeSettingsChange ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              '& .MuiButton-root': { whiteSpace: 'nowrap', flexShrink: 0 },
            }}
          >
            {showMergeResolutionControls ? (
              <>
            <DropdownFieldTooltip title={getTooltipTextForState('merge-document-missing-resolution', true)}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="compare-missing-resolve-label">Missing</InputLabel>
              <Select
                labelId="compare-missing-resolve-label"
                label="Missing"
                value={resolveMissing}
                onChange={(e) =>
                  onMergeSettingsChange(e.target.value as MergeMissingResolve, resolveTypeMismatch)
                }
              >
                <MenuItem value={MergeMissingResolve.COPY}>Copy</MenuItem>
                <MenuItem value={MergeMissingResolve.SKIP}>Skip</MenuItem>
                <MenuItem value={MergeMissingResolve.ABORT}>Abort</MenuItem>
              </Select>
              </FormControl>
            </DropdownFieldTooltip>
            <DropdownFieldTooltip title={getTooltipTextForState('merge-document-type-resolution', true)}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="compare-type-resolve-label">Type mismatch</InputLabel>
              <Select
                labelId="compare-type-resolve-label"
                label="Type mismatch"
                value={resolveTypeMismatch}
                onChange={(e) =>
                  onMergeSettingsChange(resolveMissing, e.target.value as MergeTypeMismatchResolve)
                }
              >
                <MenuItem value={MergeTypeMismatchResolve.KEEP}>Keep</MenuItem>
                <MenuItem value={MergeTypeMismatchResolve.SKIP}>Skip</MenuItem>
                <MenuItem value={MergeTypeMismatchResolve.ABORT}>Abort</MenuItem>
              </Select>
              </FormControl>
            </DropdownFieldTooltip>
              </>
            ) : null}
            {onUndoLastCopy && canUndoLastCopy ? (
              <CompareHelpTooltip
                helpKey="document-merge-compare-undo-last-copy"
                enabled={!mergeApplying}
              >
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  disabled={mergeApplying}
                  onClick={() => void onUndoLastCopy()}
                >
                  {mergeApplying ? 'Undoing…' : 'Undo last action'}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {onMergeSelectedContent && showHeaderMergeToLeft ? (
              <CompareHelpTooltip
                helpKey="document-merge-compare-merge-to-left"
                enabled={!mergeApplying}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  disabled={mergeApplying}
                  onClick={() => void handleMergeCategorySubtreeToLeft()}
                >
                  {mergeApplying
                    ? 'Merging…'
                    : workspaceKind === 'new-document'
                      ? mergeIntoTargetLabel()
                      : workspaceKind === 'view-only-source'
                        ? mergeToYourDocumentLabel()
                        : mergeToLeftActionLabel()}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {onMergeSelectedContent &&
            !isSelectiveMerge &&
            !rightIsReadOnlyShared &&
            showHeaderMergeToRight ? (
              <CompareHelpTooltip helpKey="document-merge-compare-merge-to-right" enabled={!mergeApplying}>
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  disabled={mergeApplying}
                  onClick={() => void handleMergeCategorySubtreeToRight()}
                >
                  {mergeApplying ? 'Merging…' : mergeToRightActionLabel()}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {showHeaderTypeConflictMergeToLeft ? (
              <CompareHelpTooltip
                helpKey="document-merge-compare-type-conflict-merge"
                enabled={!mergeApplying}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  disabled={mergeApplying}
                  onClick={() => void handleMergeTypeConflict('left')}
                >
                  {mergeApplying
                    ? 'Merging…'
                    : mergeToLeftActionLabel(typeConflictRowsForActions.length)}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {showHeaderTypeConflictMergeToRight ? (
              <CompareHelpTooltip
                helpKey="document-merge-compare-type-conflict-merge"
                enabled={!mergeApplying}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  disabled={mergeApplying}
                  onClick={() => void handleMergeTypeConflict('right')}
                >
                  {mergeApplying
                    ? 'Merging…'
                    : mergeToRightActionLabel(typeConflictRowsForActions.length)}
                </Button>
              </CompareHelpTooltip>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <SplitPane
          persistKey="document_compare_trees_detail_split"
          initialSplitPosition={56}
          minSplitPosition={30}
          maxSplitPosition={75}
          leftPane={
            <SplitPane
              persistKey="document_compare_left_right_split"
              initialSplitPosition={50}
              minSplitPosition={20}
              maxSplitPosition={80}
              leftPane={
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: leftTreePaneActive
                      ? alpha(theme.palette.primary.main, 0.04)
                      : undefined,
                    boxShadow: leftTreePaneActive
                      ? `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`
                      : undefined,
                  }}
                >
                  <CompareTreePaneHeader
                    side="left"
                    documentLabel={session.left.label}
                    readOnly={session.left.readAccessGrantId != null}
                    role={workspaceKind === 'new-document' ? 'target' : undefined}
                    isActiveSelectionSide={leftTreePaneActive}
                  />
                  <CompareHelpTooltip helpKey="document-merge-compare-trees" placement="right" block>
                  <Box sx={{ flex: 1, px: 0.5, py: 0.5, minHeight: 0, overflow: 'auto' }}>
                    <TreeView
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={leftExpanded}
                    selected={leftTreeSelectedNodeId}
                    onNodeToggle={(_, nodeIds) => handleChevronToggle('left', nodeIds)}
                    onNodeSelect={(_, nodeId) => selectPathFromNodeId('left', nodeId)}
                    sx={leftTreeViewSx}
                  >
                    {renderRootTreeItem(
                      'left',
                      session.leftRoot,
                      session.rightRoot,
                      leftRootNodeId,
                      session.leftRoot.name,
                      onMergeSelectedContent || onDeleteCategory ? handleTreeContextMenu : undefined,
                    )}
                  </TreeView>
                </Box>
                </CompareHelpTooltip>
                </Box>
              }
              rightPane={
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: rightTreePaneActive
                      ? alpha(theme.palette.primary.main, 0.04)
                      : rightIsReadOnlyShared
                        ? alpha(theme.palette.info.main, 0.04)
                        : undefined,
                    boxShadow: rightTreePaneActive
                      ? `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`
                      : undefined,
                  }}
                >
                  <CompareTreePaneHeader
                    side="right"
                    documentLabel={session.right.label}
                    readOnly={rightIsReadOnlyShared}
                    role={workspaceKind === 'new-document' ? 'source' : undefined}
                    isActiveSelectionSide={rightTreePaneActive}
                  />
                  <CompareHelpTooltip helpKey="document-merge-compare-trees" placement="left" block>
                  <Box sx={{ flex: 1, px: 0.5, py: 0.5, minHeight: 0, overflow: 'auto' }}>
                    <TreeView
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={rightExpanded}
                    selected={rightTreeSelectedNodeId}
                    onNodeToggle={(_, nodeIds) => handleChevronToggle('right', nodeIds)}
                    onNodeSelect={(_, nodeId) => selectPathFromNodeId('right', nodeId)}
                    sx={rightTreeViewSx}
                  >
                    {renderRootTreeItem(
                      'right',
                      session.rightRoot,
                      session.leftRoot,
                      rightRootNodeId,
                      session.rightRoot.name,
                      onMergeSelectedContent || onDeleteCategory ? handleTreeContextMenu : undefined,
                    )}
                  </TreeView>
                </Box>
                </CompareHelpTooltip>
                </Box>
              }
            />
          }
          rightPane={
        <Box sx={{ height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box
            sx={{
              px: 2,
              py: 0.75,
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {showDetailPaneActions ? (
              <CompareHelpTooltip helpKey="document-merge-compare-detail-pane" placement="bottom">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.5 }}>
                  {paneTitle}
                  {panePathLabel ? ` — ${panePathLabel}` : ''}
                </Typography>
                {showSelectAllRight && (
                  <CompareHelpTooltip helpKey="document-merge-compare-select-all-right">
                  <Button
                    size="small"
                    variant="text"
                    disableElevation
                    sx={selectAllSideButtonSx(
                      'right-only',
                      allRightOnlySelected ? 'all' : someRightOnlySelected ? 'some' : 'none',
                    )}
                    onClick={() => toggleSelectAllWithStatus('right-only')}
                  >
                    {allRightOnlySelected
                      ? oneWayFromSourceLabels
                        ? `All source-side selected (${activeRightOnlyKeys.length})`
                        : `All right selected (${activeRightOnlyKeys.length})`
                      : oneWayFromSourceLabels
                        ? `Select all from source (${activeRightOnlyKeys.length})`
                        : `Select all right (${activeRightOnlyKeys.length})`}
                  </Button>
                  </CompareHelpTooltip>
                )}
                {showSelectAllLeft && (
                  <CompareHelpTooltip helpKey="document-merge-compare-select-all-left">
                  <Button
                    size="small"
                    variant="text"
                    disableElevation
                    sx={selectAllSideButtonSx(
                      'left-only',
                      allLeftOnlySelected ? 'all' : someLeftOnlySelected ? 'some' : 'none',
                    )}
                    onClick={() => toggleSelectAllWithStatus('left-only')}
                  >
                    {allLeftOnlySelected
                      ? `All left selected (${activeLeftOnlyKeys.length})`
                      : `Select all left (${activeLeftOnlyKeys.length})`}
                  </Button>
                  </CompareHelpTooltip>
                )}
                {(showCopyActions || showDetailPaneTypeConflictMerge) && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {showDetailPaneTypeConflictMerge && typeConflictMergeActions.canLeft && (
                      <CompareHelpTooltip
                        helpKey="document-merge-compare-type-conflict-merge"
                        enabled={!mergeApplying}
                      >
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={mergeApplying}
                        onClick={() => void handleMergeTypeConflict('left')}
                      >
                        {mergeApplying
                          ? 'Merging…'
                          : mergeToLeftActionLabel(typeConflictRowsForActions.length)}
                      </Button>
                      </CompareHelpTooltip>
                    )}
                    {showDetailPaneTypeConflictMerge &&
                    !copyToRightDisabled &&
                    typeConflictMergeActions.canRight && (
                      <CompareHelpTooltip
                        helpKey="document-merge-compare-type-conflict-merge"
                        enabled={!mergeApplying}
                      >
                      <Button
                        size="small"
                        variant="contained"
                        color="info"
                        disabled={mergeApplying}
                        onClick={() => void handleMergeTypeConflict('right')}
                      >
                        {mergeApplying
                          ? 'Merging…'
                          : mergeToRightActionLabel(typeConflictRowsForActions.length)}
                      </Button>
                      </CompareHelpTooltip>
                    )}
                    {showCopyActions && (
                      <>
                        {showCopyToLeftButton && (
                          <CompareHelpTooltip
                            helpKey="document-merge-compare-copy-to-left"
                            enabled={
                              !mergeApplying &&
                              (singleSidedContentCopyPlan?.direction === 'left' ||
                                branchSubcategoriesReadyForCopyLeft ||
                                copyToLeftCount > 0)
                            }
                          >
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={
                              mergeApplying ||
                              (singleSidedContentCopyPlan?.direction === 'left'
                                ? false
                                : isSingleSidedBranchCopyLeft
                                  ? !branchSubcategoriesReadyForCopyLeft
                                  : copyToLeftCount === 0)
                            }
                            onClick={() => void handleCopyToLeft()}
                          >
                            {copyToLeftButtonLabel}
                          </Button>
                          </CompareHelpTooltip>
                        )}
                        {showCopyToRightButton && (
                          <CompareHelpTooltip
                            helpKey="document-merge-compare-copy-to-right"
                            enabled={
                              !mergeApplying &&
                              (singleSidedContentCopyPlan?.direction === 'right' ||
                                branchSubcategoriesReadyForCopyRight ||
                                copyToRightCount > 0)
                            }
                          >
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            disabled={
                              mergeApplying ||
                              (singleSidedContentCopyPlan?.direction === 'right'
                                ? false
                                : isSingleSidedBranchCopyRight
                                  ? !branchSubcategoriesReadyForCopyRight
                                  : copyToRightCount === 0)
                            }
                            onClick={() => void handleCopyToRight()}
                          >
                            {copyToRightButtonLabel}
                          </Button>
                          </CompareHelpTooltip>
                        )}
                      </>
                    )}
                  </Box>
                )}
                {copyToRightDisabled && canMergeCompare && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ width: '100%', mt: 0.25 }}
                  >
                    {isSelectiveMerge
                      ? 'Only source-side rows can be selected.'
                      : 'View-only source — only right-side rows can be copied.'}
                  </Typography>
                )}
                </Box>
                {contentStatusFilterToggle}
              </Box>
              </CompareHelpTooltip>
            ) : (
              <CompareHelpTooltip helpKey="document-merge-compare-detail-pane" placement="bottom">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 0, flex: 1 }}>
                  {paneTitle}
                  {panePathLabel ? ` — ${panePathLabel}` : ''}
                </Typography>
                {contentStatusFilterToggle}
              </Box>
              </CompareHelpTooltip>
            )}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {categoryPanelMessage && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                {categoryPanelMessage}
              </Typography>
            )}
            {isTypeConflictSelection && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0 }}>
                Type conflict — {activeDocumentLabel}. Select the other structure to compare sides.
                {showHeaderTypeConflictMerge
                  ? ' Use header Merge to resolve.'
                  : typeConflictCategoryRowKeys.length > 0
                    ? ' Select rows below or use Copy.'
                    : ''}
              </Typography>
            )}
            {singleSidedBranchCopyView && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0 }}>
                Check at least two subcategories to copy this branch selectively, or use header
                Merge for the whole branch.
              </Typography>
            )}
            {viewingContentUnderSingleSidedBranch && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0 }}>
                Rows are read-only. Select the branch in the category structure and use header Merge to copy.
              </Typography>
            )}
            {comparedCategoryRows && (
              <Table
                size="small"
                stickyHeader
                sx={{
                  fontSize: 'var(--app-font-size, 14px)',
                  '& .MuiTableCell-root': {
                    padding: '4px 8px',
                    fontSize: 'var(--app-font-size, 14px)',
                  },
                  '& .MuiTableRow-root': {
                    height: 36,
                    cursor: 'pointer',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: theme.palette.background.paper,
                    fontWeight: 600,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {showCategoryCheckboxes ? (
                      <TableCell padding="checkbox" sx={{ width: 42 }} />
                    ) : null}
                    <TableCell sx={{ width: 36 }}>Type</TableCell>
                    <TableCell>Name</TableCell>
                    {showCategoryCheckboxes ? (
                      <TableCell sx={{ width: 100 }}>Match</TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparedCategoryRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showCategoryCheckboxes ? 4 : 2}>
                        <Typography variant="body2" color="text.secondary">
                          No subcategories under this category.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    comparedCategoryRows.map((row) => {
                      const node =
                        row.leftCategory ?? row.rightCategory;
                      const selectionKey = comparedCategorySelectionKey(row);
                      const canSelect =
                        !!onMergeSelectedContent &&
                        comparedCategoryCanSelectForMerge(row, copyToRightDisabled) &&
                        (canMergeCompare || row.status === 'type-conflict');
                      const isPathSelected =
                        selectedPath != null &&
                        selectedPath.length === row.childRelPath.length &&
                        row.childRelPath.every((p, i) => selectedPath[i] === p);
                      return (
                        <TableRow
                          key={row.key}
                          hover
                          selected={isPathSelected}
                          onClick={() => {
                            const side =
                              row.status === 'right-only'
                                ? 'right'
                                : row.status === 'left-only'
                                  ? 'left'
                                  : selectedTreeSide;
                            selectPath(side, row.childRelPath);
                          }}
                          sx={{
                            backgroundColor: categoryRowBackground(row.status),
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(
                                row.status === 'match'
                                  ? theme.palette.success.main
                                  : row.status === 'left-only'
                                    ? theme.palette.info.main
                                    : row.status === 'right-only' ||
                                        row.status === 'missing-content' ||
                                        row.status === 'has-extra-content' ||
                                        row.status === 'has-child-diff'
                                      ? theme.palette.warning.main
                                      : theme.palette.error.main,
                                0.18,
                              ),
                            },
                          }}
                        >
                          {showCategoryCheckboxes ? (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              {canSelect ? (
                                <Checkbox
                                  size="small"
                                  checked={selectedRowKeys.has(selectionKey)}
                                  onChange={() => toggleRowSelection(selectionKey)}
                                  onClick={(e) => e.stopPropagation()}
                                  inputProps={{ 'aria-label': `Select ${row.displayName}` }}
                                />
                              ) : null}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            {renderCompareChildCategoryTypeIcon(node, row.status)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.displayName}</Typography>
                          </TableCell>
                          {showCategoryCheckboxes ? (
                            <TableCell>
                              {row.status !== 'unpaired' ? (
                                <CompareStatusChip
                                  status={row.status}
                                  oneWayLabels={oneWayFromSourceLabels}
                                  newDocumentWorkspace={workspaceKind === 'new-document'}
                                  existsOnTarget={row.leftCategory != null}
                                  underOneSidedBranch={underOneSidedBranchContext}
                                />
                              ) : null}
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
            {comparedContentRows && (
              <Table
                size="small"
                stickyHeader
                sx={{
                  fontSize: 'var(--app-font-size, 14px)',
                  '& .MuiTableCell-root': {
                    padding: '4px 8px',
                    fontSize: 'var(--app-font-size, 14px)',
                  },
                  '& .MuiTableRow-root': {
                    height: 32,
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: theme.palette.background.paper,
                    fontWeight: 600,
                  },
                  '@media (hover: hover)': {
                    '& .compare-content-row-actions': {
                      opacity: 0,
                      pointerEvents: 'none',
                      transition: 'opacity 120ms ease',
                    },
                    '& .MuiTableRow-root:hover .compare-content-row-actions': {
                      opacity: 1,
                      pointerEvents: 'auto',
                    },
                    '& .compare-content-row-actions:focus-within': {
                      opacity: 1,
                      pointerEvents: 'auto',
                    },
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {canMergeCompare ? (
                      <TableCell padding="checkbox" sx={{ width: 42 }} />
                    ) : null}
                    <TableCell sx={{ width: 36 }} />
                    {showContentCategoryColumn ? (
                      <TableCell sx={{ width: '18%' }}>Category</TableCell>
                    ) : null}
                    <TableCell sx={{ width: '20%' }}>Subcase</TableCell>
                    <TableCell>Content</TableCell>
                    {showContentMatchColumn ? (
                      <TableCell sx={{ width: 100 }}>Match</TableCell>
                    ) : null}
                    <TableCell sx={{ width: 72, px: 0.5 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparedContentRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          4 +
                          (showContentCategoryColumn ? 1 : 0) +
                          (showContentMatchColumn ? 1 : 0)
                        }
                      >
                        <Typography variant="body2" color="text.secondary">
                          No content rows in this category.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : displayedComparedContentRows!.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          4 +
                          (showContentCategoryColumn ? 1 : 0) +
                          (showContentMatchColumn ? 1 : 0)
                        }
                      >
                        <Typography variant="body2" color="text.secondary">
                          No rows match the current filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedComparedContentRows!.map((row) => {
                      const { content, status } = row;
                      const selectionKey = comparedRowSelectionKey(row);
                      const canSelect =
                        canMergeCompare &&
                        !!onMergeSelectedContent &&
                        comparedRowCanSelectForMerge(row, copyToRightDisabled);
                      const leftDeleteTarget = resolveCompareContentDeleteTarget(
                        row,
                        'left',
                        selectedLeft,
                        selectedRight,
                      );
                      const rightDeleteTarget = resolveCompareContentDeleteTarget(
                        row,
                        'right',
                        selectedLeft,
                        selectedRight,
                      );
                      const hasRowDeleteActions =
                        showContentDeleteActions &&
                        ((leftDeleteTarget && canDeleteOnSide?.('left')) ||
                          (rightDeleteTarget && canDeleteOnSide?.('right')));
                      const canPreviewRow = compareRowHasPreviewableContent(row);
                      return (
                        <TableRow
                          key={row.key}
                          hover
                          selected={canSelect && selectedRowKeys.has(selectionKey)}
                          sx={{
                            backgroundColor: contentRowBackground(status),
                            '&:hover': {
                              backgroundColor: viewingContentUnderSingleSidedBranch
                                ? alpha(theme.palette.action.hover, 0.08)
                                : alpha(
                                    status === 'both'
                                      ? theme.palette.success.main
                                      : status === 'left-only'
                                        ? theme.palette.info.main
                                        : theme.palette.warning.main,
                                    0.18,
                                  ),
                            },
                          }}
                        >
                          {canMergeCompare ? (
                            <TableCell padding="checkbox">
                              {canSelect ? (
                                <Checkbox
                                  size="small"
                                  checked={selectedRowKeys.has(selectionKey)}
                                  onChange={() => toggleRowSelection(selectionKey)}
                                  onClick={(e) => e.stopPropagation()}
                                  inputProps={{ 'aria-label': `Select ${content.subcase}` }}
                                />
                              ) : null}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <StickyNote2OutlinedIcon
                                sx={{ color: 'text.secondary', fontSize: 20 }}
                                aria-hidden
                              />
                              {contentHasMedia(content) ? (
                                <Tooltip
                                  title={getContentMediaIndicatorTooltip(content)}
                                  placement="top"
                                >
                                  <PermMediaOutlinedIcon
                                    sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }}
                                    aria-label="Has media"
                                  />
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                          {showContentCategoryColumn ? (
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color:
                                    (row.categoryRelPath?.length ?? 0) > 0
                                      ? 'text.primary'
                                      : 'text.secondary',
                                }}
                              >
                                {(row.categoryRelPath?.length ?? 0) > 0
                                  ? row.categoryRelPath!.join(' / ')
                                  : '—'}
                              </Typography>
                            </TableCell>
                          ) : null}
                          <TableCell>{content.subcase || '(no subcase)'}</TableCell>
                          <TableCell
                            onClick={
                              canPreviewRow
                                ? () => {
                                    openCompareContentPreview(row);
                                    (document.activeElement as HTMLElement | null)?.blur?.();
                                  }
                                : undefined
                            }
                            sx={
                              canPreviewRow
                                ? {
                                    cursor: 'pointer',
                                    '&:hover .compare-content-preview-text': {
                                      textDecoration: 'underline',
                                    },
                                  }
                                : undefined
                            }
                          >
                            <Typography
                              component="span"
                              variant="body2"
                              className="compare-content-preview-text"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: content.text.trim() ? 'text.primary' : 'text.secondary',
                                fontStyle: content.text.trim() ? 'normal' : 'italic',
                              }}
                            >
                              {formatContentPreview(content)}
                            </Typography>
                          </TableCell>
                          {showContentMatchColumn ? (
                            <TableCell>
                              <CompareContentStatusChip
                                status={status}
                                oneWayLabels={oneWayFromSourceLabels}
                                newDocumentWorkspace={workspaceKind === 'new-document'}
                                underOneSidedBranch={underOneSidedBranchContext}
                              />
                            </TableCell>
                          ) : null}
                          <TableCell padding="none">
                            <Box
                              className={
                                canPreviewRow || hasRowDeleteActions
                                  ? 'compare-content-row-actions'
                                  : undefined
                              }
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 0.25,
                                minHeight: 28,
                                alignItems: 'center',
                              }}
                            >
                              {canPreviewRow ? (
                                <Tooltip title="View full content">
                                  <IconButton
                                    size="small"
                                    aria-label={`View full content for ${content.subcase || 'row'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCompareContentPreview(row);
                                      (e.currentTarget as HTMLElement).blur();
                                    }}
                                  >
                                    <VisibilityOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              {showContentDeleteActions &&
                              leftDeleteTarget &&
                              canDeleteOnSide?.('left') ? (
                                  <Tooltip title="Delete from left document">
                                    <IconButton
                                      size="small"
                                      aria-label="Delete content from left"
                                      data-testid={`compare-delete-content-left-${leftDeleteTarget.content.id}`}
                                      onClick={(e) => {
                                        onDeleteContent!(
                                          'left',
                                          leftDeleteTarget.content,
                                          leftDeleteTarget.categoryId,
                                          leftDeleteTarget.parentName,
                                        );
                                        (e.currentTarget as HTMLElement).blur();
                                      }}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              {showContentDeleteActions &&
                              rightDeleteTarget &&
                              canDeleteOnSide?.('right') ? (
                                  <Tooltip title="Delete from right document">
                                    <IconButton
                                      size="small"
                                      aria-label="Delete content from right"
                                      data-testid={`compare-delete-content-right-${rightDeleteTarget.content.id}`}
                                      onClick={(e) => {
                                        onDeleteContent!(
                                          'right',
                                          rightDeleteTarget.content,
                                          rightDeleteTarget.categoryId,
                                          rightDeleteTarget.parentName,
                                        );
                                        (e.currentTarget as HTMLElement).blur();
                                      }}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>
          }
        />
      </Box>
      <Menu
        open={treeContextMenu != null}
        onClose={closeTreeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          treeContextMenu
            ? { top: treeContextMenu.mouseY, left: treeContextMenu.mouseX }
            : undefined
        }
      >
        {selectedCategoryDeleteContext ? (
          <MenuItem
            data-testid="compare-tree-menu-delete-category"
            disabled={mergeApplying || !selectedCategoryDeleteContext.allowed}
            onClick={() => {
              if (!selectedCategoryDeleteContext.allowed) {
                return;
              }
              closeTreeContextMenu();
              onDeleteCategory?.(
                selectedCategoryDeleteContext.side,
                selectedCategoryDeleteContext.category,
                selectedCategoryDeleteContext.parentName,
                selectedCategoryDeleteContext.parentId,
                selectedCategoryDeleteContext.index,
              );
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            Delete category
          </MenuItem>
        ) : null}
        {showHeaderMergeToLeft ? (
          <MenuItem
            data-testid="compare-tree-menu-merge-to-left"
            disabled={mergeApplying}
            onClick={() => {
              closeTreeContextMenu();
              void handleMergeCategorySubtreeToLeft();
            }}
          >
            <ListItemIcon>
              <CallMergeIcon fontSize="small" />
            </ListItemIcon>
            {workspaceKind === 'new-document'
              ? mergeIntoTargetLabel()
              : workspaceKind === 'view-only-source'
                ? mergeToYourDocumentLabel()
                : mergeToLeftActionLabel()}
          </MenuItem>
        ) : null}
        {showHeaderMergeToRight ? (
          <MenuItem
            data-testid="compare-tree-menu-merge-to-right"
            disabled={mergeApplying}
            onClick={() => {
              closeTreeContextMenu();
              void handleMergeCategorySubtreeToRight();
            }}
          >
            <ListItemIcon>
              <CallMergeIcon fontSize="small" />
            </ListItemIcon>
            {mergeToRightActionLabel()}
          </MenuItem>
        ) : null}
        {showHeaderTypeConflictMergeToLeft ? (
          <MenuItem
            data-testid="compare-tree-menu-type-conflict-left"
            disabled={mergeApplying}
            onClick={() => {
              closeTreeContextMenu();
              void handleMergeTypeConflict('left');
            }}
          >
            <ListItemIcon>
              <CallMergeIcon fontSize="small" />
            </ListItemIcon>
            {mergeToLeftActionLabel(typeConflictRowsForActions.length)}
          </MenuItem>
        ) : null}
        {showHeaderTypeConflictMergeToRight ? (
          <MenuItem
            data-testid="compare-tree-menu-type-conflict-right"
            disabled={mergeApplying}
            onClick={() => {
              closeTreeContextMenu();
              void handleMergeTypeConflict('right');
            }}
          >
            <ListItemIcon>
              <CallMergeIcon fontSize="small" />
            </ListItemIcon>
            {mergeToRightActionLabel(typeConflictRowsForActions.length)}
          </MenuItem>
        ) : null}
        {!compareTreeCategoryMenuHasActions ? (
          <MenuItem disabled>No actions for this category</MenuItem>
        ) : null}
      </Menu>
      <ContentRichPreviewDialog
        open={contentPreview != null}
        onClose={() => {
          setContentPreview(null);
          (document.activeElement as HTMLElement | null)?.blur?.();
        }}
        html={contentPreview?.views[0]?.html ?? ''}
        views={contentPreview?.views}
        title={contentPreview?.title ?? 'Content preview'}
      />
    </Box>
  );
};

export default DocumentCompareView;
